import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, Clock, MapPin, Users, ArrowLeft, X, Save, Trash2, Mail, Bell, Repeat, Loader2, CheckSquare, PanelRight, Inbox, Edit2, List, CalendarClock, Music, Sparkles, Zap, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { utcToUserTimezone, userTimezoneToUtc, utcToDatetimeLocal, datetimeLocalToUtc, getDayBoundariesUtc, getWeekBoundariesUtc, getMonthBoundariesUtc, formatTimeInTimezone, createDateAtTimeInTimezone, detectBrowserTimezone } from '../utils/timezone';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const EVENT_COLORS = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Green', value: '#10B981' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
];
export function CalendarApp() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [taskLists, setTaskLists] = useState([]);
    const [lengths, setLengths] = useState([]);
    const [viewMode, setViewMode] = useState('day');
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isTaskListMode, setIsTaskListMode] = useState(false); // Track if editing a task_list
    const [isLengthMode, setIsLengthMode] = useState(false); // Track if editing a length
    const [showCreateDropdown, setShowCreateDropdown] = useState(false); // Show create type dropdown
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showRightPanel, setShowRightPanel] = useState(true);
    const [showLeftPanel, setShowLeftPanel] = useState(false);
    const [showListPanel, setShowListPanel] = useState(false);
    const [showTopWeekPanel, setShowTopWeekPanel] = useState(false);
    const [unscheduledSteps, setUnscheduledSteps] = useState([]);
    const [showQuickEditModal, setShowQuickEditModal] = useState(false);
    const [quickEditStep, setQuickEditStep] = useState(null);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [rescheduleEvent, setRescheduleEvent] = useState(null);
    const [userTimezone, setUserTimezone] = useState('America/Los_Angeles'); // User's preferred timezone
    const [eventForm, setEventForm] = useState({
        label: '',
        description: '',
        location: '',
        scheduled_start_time: undefined,
        scheduled_end_time: undefined,
        all_day: false,
        attendees: [],
        color: '#3B82F6',
        status: 'confirmed',
        isbuildhabit: false, // Default to activity (not habit)
        duration: 60, // Default 60 minutes
        completed: false,
        // Mauna-app fields
        mantra: '',
        starting_ritual: '',
        ending_ritual: '',
        audio_url: null,
        audio_type: null,
        journal_content: '',
        cbt_notes: '',
        dddpt_discover: '',
        dddpt_define: '',
        dddpt_ideate: '',
        dddpt_prototype: '',
        dddpt_test: '',
        energy_level: 3,
        priority_letter: 'None',
        priority_rank: 0,
        locked: false,
        alerts: [],
    });
    const [breaths, setBreaths] = useState([]);
    const [newBreathName, setNewBreathName] = useState('');
    const [breathCounts, setBreathCounts] = useState(new Map());
    const [linkedSteps, setLinkedSteps] = useState([]); // Steps linked to a task_list
    const [newStepName, setNewStepName] = useState(''); // For adding new steps to task_list
    const [showUpdateScopeModal, setShowUpdateScopeModal] = useState(false); // Show update scope modal for recurring events
    const [updateScope, setUpdateScope] = useState('this'); // Which instances to update
    // Helper function to extract actual ID from composite ID
    // Composite IDs have format: habitId-YYYY-MM-DD
    const extractActualId = (id, isHabitInstance) => {
        if (!isHabitInstance)
            return id;
        // Use regex to match the actual ID up to a date pattern at the end
        const match = id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/);
        return match ? match[1] : id;
    };
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const days = [];
        // Add empty cells for days before month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        // Add days of month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        return days;
    };
    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };
    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };
    const prevWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentDate(newDate);
    };
    const nextWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentDate(newDate);
    };
    const prevDay = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 1);
        setCurrentDate(newDate);
    };
    const nextDay = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 1);
        setCurrentDate(newDate);
    };
    const handlePrev = () => {
        if (viewMode === 'month')
            prevMonth();
        else if (viewMode === 'week')
            prevWeek();
        else
            prevDay();
    };
    const handleNext = () => {
        if (viewMode === 'month')
            nextMonth();
        else if (viewMode === 'week')
            nextWeek();
        else
            nextDay();
    };
    const getWeekDays = (date) => {
        const day = date.getDay();
        const diff = date.getDate() - day; // Get Sunday
        const sunday = new Date(date);
        sunday.setDate(diff);
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const weekDay = new Date(sunday);
            weekDay.setDate(sunday.getDate() + i);
            weekDays.push(weekDay);
        }
        return weekDays;
    };
    // Get maximum spillover hours needed for current week/day view
    const getMaxSpilloverHours = () => {
        let maxSpilloverHours = 0;
        events.forEach(event => {
            if (!event.scheduled_start_time || !event.scheduled_end_time)
                return;
            const start = new Date(event.scheduled_start_time);
            const end = new Date(event.scheduled_end_time);
            // Calculate hours beyond the start day
            const startDayEnd = new Date(start);
            startDayEnd.setHours(23, 59, 59, 999);
            if (end > startDayEnd) {
                const spilloverMs = end.getTime() - startDayEnd.getTime();
                const spilloverHours = Math.ceil(spilloverMs / (1000 * 60 * 60));
                maxSpilloverHours = Math.max(maxSpilloverHours, spilloverHours);
            }
        });
        return maxSpilloverHours;
    };
    const getTimeSlots = () => {
        const slots = [];
        const spilloverHours = getMaxSpilloverHours();
        const totalHours = 24 + spilloverHours;
        for (let hour = 0; hour < totalHours; hour++) {
            if (hour < 24) {
                slots.push(`${hour.toString().padStart(2, '0')}:00`);
            }
            else {
                // Show as "+1 day", "+2 days", etc.
                const dayOffset = Math.floor(hour / 24);
                const hourInDay = hour % 24;
                slots.push(`+${dayOffset}d ${hourInDay.toString().padStart(2, '0')}:00`);
            }
        }
        return slots;
    };
    const getEventsForDateTime = (date, hour) => {
        return events.filter(event => {
            if (!event.scheduled_start_time || !event.scheduled_end_time)
                return false;
            const eventStart = new Date(event.scheduled_start_time);
            const eventEnd = new Date(event.scheduled_end_time);
            // Create a datetime for this cell
            const cellDateTime = new Date(date);
            cellDateTime.setHours(hour % 24, 0, 0, 0);
            // If hour >= 24, we're in the next day(s)
            if (hour >= 24) {
                const daysToAdd = Math.floor(hour / 24);
                cellDateTime.setDate(cellDateTime.getDate() + daysToAdd);
            }
            const cellEndTime = new Date(cellDateTime);
            cellEndTime.setHours(cellDateTime.getHours() + 1);
            // Check if event overlaps with this time cell
            // Event starts on the same day as the cell date
            const eventStartDay = new Date(eventStart);
            eventStartDay.setHours(0, 0, 0, 0);
            const cellDay = new Date(date);
            cellDay.setHours(0, 0, 0, 0);
            // Only show events that START on this day
            if (eventStartDay.getTime() !== cellDay.getTime()) {
                return false;
            }
            // Check if the event is active during this hour
            return eventStart < cellEndTime && eventEnd > cellDateTime;
        });
    };
    const isToday = (day) => {
        const today = new Date();
        return (day === today.getDate() &&
            currentDate.getMonth() === today.getMonth() &&
            currentDate.getFullYear() === today.getFullYear());
    };
    const isSelectedDate = (day) => {
        return (day === selectedDate.getDate() &&
            currentDate.getMonth() === selectedDate.getMonth() &&
            currentDate.getFullYear() === selectedDate.getFullYear());
    };
    // Helper function to generate recurring event instances
    const generateRecurringInstances = (baseEvent, startDate, endDate) => {
        if (!baseEvent.isbuildhabit || !baseEvent.frequency || !baseEvent.scheduled_start_time) {
            return [baseEvent]; // Non-recurring event
        }
        const instances = [];
        const eventStartTime = new Date(baseEvent.scheduled_start_time);
        const eventEndTime = baseEvent.scheduled_end_time ? new Date(baseEvent.scheduled_end_time) : null;
        // Get the time of day from the original event (use UTC methods to preserve absolute UTC time)
        const startHours = eventStartTime.getUTCHours();
        const startMinutes = eventStartTime.getUTCMinutes();
        const startSeconds = eventStartTime.getUTCSeconds();
        const duration = eventEndTime
            ? (eventEndTime.getTime() - eventStartTime.getTime()) / 1000 / 60 // minutes
            : baseEvent.duration;
        // Parse excluded dates into a Set for O(1) lookup
        const excludedDates = new Set();
        if (baseEvent.recurrence_exception_dates && Array.isArray(baseEvent.recurrence_exception_dates)) {
            baseEvent.recurrence_exception_dates.forEach(dateStr => {
                // Normalize to date-only string (YYYY-MM-DD) for comparison using UTC
                const date = new Date(dateStr);
                const normalized = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
                excludedDates.add(normalized);
            });
        }
        // Parse frequency pattern
        const frequency = baseEvent.frequency.toLowerCase();
        const daysOfWeek = [];
        if (frequency === 'daily' || frequency === 'every day!') {
            daysOfWeek.push(0, 1, 2, 3, 4, 5, 6); // All days
        }
        else if (frequency === 'weekdays' || frequency === 'monday, tuesday, wednesday, thursday, friday') {
            daysOfWeek.push(1, 2, 3, 4, 5); // Mon-Fri
        }
        else if (frequency === 'weekends' || frequency === 'saturday, sunday') {
            daysOfWeek.push(0, 6); // Sat-Sun
        }
        else {
            // Parse individual days like "Monday, Wednesday, Friday"
            const dayMap = {
                'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
                'thursday': 4, 'friday': 5, 'saturday': 6
            };
            frequency.split(',').forEach(day => {
                const trimmedDay = day.trim().toLowerCase();
                if (dayMap[trimmedDay] !== undefined) {
                    daysOfWeek.push(dayMap[trimmedDay]);
                }
            });
        }
        // Generate instances for the date range
        const currentDate = new Date(Math.max(startDate.getTime(), eventStartTime.getTime()));
        currentDate.setUTCHours(0, 0, 0, 0);
        const rangeEnd = new Date(endDate);
        rangeEnd.setUTCHours(23, 59, 59, 999);
        while (currentDate <= rangeEnd) {
            const dayOfWeek = currentDate.getUTCDay();
            // Check if this day matches the frequency pattern
            if (daysOfWeek.includes(dayOfWeek)) {
                // Create instance for this day
                const instanceStart = new Date(currentDate);
                instanceStart.setUTCHours(startHours, startMinutes, startSeconds, 0);
                // Only include if it's on or after the original event date
                if (instanceStart >= eventStartTime) {
                    // Check if this date is excluded
                    const instanceDateKey = `${currentDate.getUTCFullYear()}-${String(currentDate.getUTCMonth() + 1).padStart(2, '0')}-${String(currentDate.getUTCDate()).padStart(2, '0')}`;
                    if (!excludedDates.has(instanceDateKey)) {
                        const instanceEnd = new Date(instanceStart.getTime() + duration * 60 * 1000);
                        // Create unique composite ID for this recurring instance (baseId-YYYY-MM-DD)
                        const compositeId = `${baseEvent.id}-${instanceDateKey}`;
                        instances.push({
                            ...baseEvent,
                            id: compositeId, // Use composite ID to ensure uniqueness for React keys
                            scheduled_start_time: instanceStart.toISOString(),
                            scheduled_end_time: instanceEnd.toISOString(),
                            // Add a marker to identify this is a recurring instance
                            _isRecurringInstance: true,
                            _originalDate: baseEvent.scheduled_start_time,
                            _instanceDate: instanceStart.toISOString()
                        });
                    }
                }
            }
            // Move to next day
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }
        return instances;
    };
    // Load user's timezone preference from profile
    useEffect(() => {
        const loadUserTimezone = async () => {
            if (!user)
                return;
            try {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('timezone')
                    .eq('id', user.id)
                    .single();
                if (profile?.timezone) {
                    setUserTimezone(profile.timezone);
                    console.log('🌍 Loaded user timezone:', profile.timezone);
                }
                else {
                    // Fallback to browser timezone if not set
                    const browserTz = detectBrowserTimezone();
                    setUserTimezone(browserTz);
                    console.log('🌍 No timezone in profile, using browser timezone:', browserTz);
                }
            }
            catch (error) {
                console.error('Error loading timezone:', error);
                setUserTimezone(detectBrowserTimezone()); // Fallback
            }
        };
        loadUserTimezone();
    }, [user]);
    // Fetch events from database
    useEffect(() => {
        if (user) {
            fetchEvents();
            fetchTaskLists();
            fetchLengths();
            fetchUnscheduledSteps();
        }
    }, [user, currentDate, viewMode, selectedDate]);
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            if (showCreateDropdown && !target.closest('.relative')) {
                setShowCreateDropdown(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showCreateDropdown]);
    const fetchUnscheduledSteps = async () => {
        if (!user) {
            console.log('⚠️ No user found, cannot fetch unscheduled steps');
            return;
        }
        try {
            console.log('🔍 Fetching unscheduled steps for user:', user.id);
            console.log('🔍 User ID matches 130dc80a-224a-45ee-bccd-cdab744a5f63?', user.id === '130dc80a-224a-45ee-bccd-cdab744a5f63');
            const { data, error } = await supabase
                .from('steps')
                .select('*')
                .eq('user_id', user.id)
                .is('scheduled_start_time', null) // Only get unscheduled steps
                .order('created_at', { ascending: false });
            if (error) {
                console.error('❌ Error from Supabase:', error);
                throw error;
            }
            console.log('✅ Unscheduled steps fetched:', data?.length, 'steps');
            console.log('📋 Data:', data);
            setUnscheduledSteps(data || []);
        }
        catch (error) {
            console.error('❌ Error fetching unscheduled steps:', error);
        }
    };
    const fetchEvents = async () => {
        if (!user)
            return;
        setLoading(true);
        try {
            let startDate, endDate;
            if (viewMode === 'month') {
                // Get month boundaries in user's timezone, converted to UTC for database query
                const boundaries = getMonthBoundariesUtc(currentDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
                console.log('📅 Month view boundaries (UTC):', startDate.toISOString(), 'to', endDate.toISOString());
            }
            else if (viewMode === 'week') {
                // Get week boundaries in user's timezone, converted to UTC
                const boundaries = getWeekBoundariesUtc(currentDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
                console.log('📅 Week view boundaries (UTC):', startDate.toISOString(), 'to', endDate.toISOString());
            }
            else {
                // Day view - Use user timezone boundaries converted to UTC
                const boundaries = getDayBoundariesUtc(selectedDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
                console.log('📅 Day view boundaries (UTC) for', selectedDate.toLocaleDateString(), ':', startDate.toISOString(), 'to', endDate.toISOString());
            }
            // Fetch one-time scheduled events
            const { data: oneTimeEvents, error: oneTimeError } = await supabase
                .from('steps')
                .select('*')
                .eq('user_id', user.id)
                .not('scheduled_start_time', 'is', null)
                .eq('isbuildhabit', false) // Non-recurring events
                .gte('scheduled_start_time', startDate.toISOString())
                .lte('scheduled_start_time', endDate.toISOString())
                .order('scheduled_start_time', { ascending: true });
            if (oneTimeError)
                throw oneTimeError;
            console.log('📅 fetchEvents - One-time events fetched:', oneTimeEvents?.length || 0, 'from', startDate.toLocaleDateString(), 'to', endDate.toLocaleDateString());
            if (oneTimeEvents && oneTimeEvents.length > 0) {
                console.log('📅 First one-time event:', oneTimeEvents[0].label, 'isbuildhabit:', oneTimeEvents[0].isbuildhabit);
            }
            // Fetch recurring habits (with a broader date range since they repeat)
            const recurringStartDate = new Date(startDate);
            recurringStartDate.setMonth(recurringStartDate.getMonth() - 3); // Look back 3 months
            const { data: recurringHabits, error: recurringError } = await supabase
                .from('steps')
                .select('*')
                .eq('user_id', user.id)
                .not('scheduled_start_time', 'is', null)
                .eq('isbuildhabit', true) // Recurring habits
                .not('frequency', 'is', null) // Must have frequency
                .lte('scheduled_start_time', endDate.toISOString()) // Started before or during the range
                .order('scheduled_start_time', { ascending: true });
            if (recurringError)
                throw recurringError;
            // Fetch habits from Habits app (tag='habit', tracked via history array)
            const { data: dailyHabits, error: habitsError } = await supabase
                .from('steps')
                .select('*')
                .eq('user_id', user.id)
                .eq('tag', 'habit');
            if (habitsError)
                throw habitsError;
            // Get IDs of recurring habits to avoid duplicate processing
            const recurringHabitIds = new Set(recurringHabits?.map(h => h.id) || []);
            // Generate recurring instances
            let allEvents = [...(oneTimeEvents || [])];
            if (recurringHabits && recurringHabits.length > 0) {
                recurringHabits.forEach(habit => {
                    const instances = generateRecurringInstances(habit, startDate, endDate);
                    allEvents = [...allEvents, ...instances];
                });
            }
            // Generate daily habit instances (skip ones already processed as recurring)
            if (dailyHabits && dailyHabits.length > 0) {
                dailyHabits.forEach((habit) => {
                    // Skip if already processed as a recurring habit
                    if (recurringHabitIds.has(habit.id)) {
                        return;
                    }
                    // Get the scheduled time from habit_notes if available
                    const scheduledTime = habit.habit_notes?._scheduled_time || '08:00';
                    const [hours, minutes] = scheduledTime.split(':').map(Number);
                    // Create an instance for each day in the range
                    let currentDay = new Date(startDate);
                    while (currentDay <= endDate) {
                        const dateStr = currentDay.toISOString().split('T')[0];
                        // Create datetime for this instance
                        const instanceDateTime = new Date(currentDay);
                        instanceDateTime.setHours(hours, minutes, 0, 0);
                        // Check if completed for this date
                        const isCompleted = habit.history?.includes(dateStr) || false;
                        const isSkipped = habit.habit_skipped?.[dateStr] || false;
                        // Create a calendar event instance for this habit on this day
                        const habitInstance = {
                            ...habit,
                            id: `${habit.id}-${dateStr}`, // Unique ID for this instance
                            scheduled_start_time: instanceDateTime.toISOString(),
                            scheduled_end_time: new Date(instanceDateTime.getTime() + (habit.duration || 30) * 60000).toISOString(),
                            completed: isCompleted,
                            status: isSkipped ? 'cancelled' : isCompleted ? 'confirmed' : 'pending',
                            all_day: false,
                            isbuildhabit: true,
                        };
                        allEvents.push(habitInstance);
                        // Move to next day
                        currentDay.setDate(currentDay.getDate() + 1);
                    }
                });
            }
            // Sort by start time
            allEvents.sort((a, b) => {
                const aTime = a.scheduled_start_time ? new Date(a.scheduled_start_time).getTime() : 0;
                const bTime = b.scheduled_start_time ? new Date(b.scheduled_start_time).getTime() : 0;
                return aTime - bTime;
            });
            console.log('📅 fetchEvents - Total events to set:', allEvents.length);
            if (allEvents.length > 0) {
                console.log('📅 First event:', allEvents[0].label, 'at', new Date(allEvents[0].scheduled_start_time).toLocaleTimeString());
            }
            setEvents(allEvents);
            // Fetch breath counts for all events
            // Extract actual step IDs from composite IDs (habit instances have format: habitId-YYYY-MM-DD)
            const eventIds = allEvents
                .map(e => {
                if (e.id && e.isbuildhabit) {
                    // Check if this is actually a composite ID by looking for date pattern at end (YYYY-MM-DD)
                    const match = e.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/);
                    if (match) {
                        // It's a composite ID, extract the actual habit ID (group 1)
                        return match[1];
                    }
                }
                return e.id;
            })
                .filter((id, index, self) => id && self.indexOf(id) === index); // Unique IDs only
            if (eventIds.length > 0) {
                await fetchBreathCounts(eventIds);
            }
        }
        catch (error) {
            console.error('Error fetching events:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchTaskLists = async () => {
        if (!user)
            return;
        console.log('📋 fetchTaskLists called for user:', user.id);
        try {
            let startDate, endDate;
            if (viewMode === 'month') {
                // Get month boundaries in user's timezone, converted to UTC for database query
                const boundaries = getMonthBoundariesUtc(currentDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
            }
            else if (viewMode === 'week') {
                // Get week boundaries in user's timezone, converted to UTC
                const boundaries = getWeekBoundariesUtc(currentDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
            }
            else {
                // Day view - Use user timezone boundaries converted to UTC
                const boundaries = getDayBoundariesUtc(selectedDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
            }
            console.log('📋 fetchTaskLists - Query dates:', startDate.toISOString(), 'to', endDate.toISOString(), 'for view mode:', viewMode);
            // Fetch one-time scheduled task_lists
            const { data: oneTimeTaskLists, error: oneTimeError } = await supabase
                .from('task_lists')
                .select('*')
                .eq('user_id', user.id)
                .not('scheduled_start_time', 'is', null)
                .is('frequency', null) // Non-recurring
                .gte('scheduled_start_time', startDate.toISOString())
                .lte('scheduled_start_time', endDate.toISOString())
                .order('scheduled_start_time', { ascending: true });
            if (oneTimeError)
                throw oneTimeError;
            console.log('📋 fetchTaskLists - One-time task lists fetched:', oneTimeTaskLists?.length || 0);
            // Fetch recurring task_lists (with broader date range)
            const recurringStartDate = new Date(startDate);
            recurringStartDate.setMonth(recurringStartDate.getMonth() - 3);
            const { data: recurringTaskLists, error: recurringError } = await supabase
                .from('task_lists')
                .select('*')
                .eq('user_id', user.id)
                .not('scheduled_start_time', 'is', null)
                .not('frequency', 'is', null) // Must have frequency
                .lte('scheduled_start_time', endDate.toISOString())
                .order('scheduled_start_time', { ascending: true });
            if (recurringError)
                throw recurringError;
            console.log('📋 fetchTaskLists - Recurring task lists fetched:', recurringTaskLists?.length || 0);
            // Generate recurring instances
            let allTaskLists = [...(oneTimeTaskLists || [])];
            if (recurringTaskLists && recurringTaskLists.length > 0) {
                recurringTaskLists.forEach(taskList => {
                    const instances = generateRecurringInstances(taskList, startDate, endDate);
                    allTaskLists = [...allTaskLists, ...instances];
                });
            }
            // Sort by start time
            allTaskLists.sort((a, b) => {
                const aTime = a.scheduled_start_time ? new Date(a.scheduled_start_time).getTime() : 0;
                const bTime = b.scheduled_start_time ? new Date(b.scheduled_start_time).getTime() : 0;
                return aTime - bTime;
            });
            console.log('📋 fetchTaskLists - Total task lists to set:', allTaskLists.length);
            setTaskLists(allTaskLists);
        }
        catch (error) {
            console.error('Error fetching task lists:', error);
        }
    };
    const fetchLengths = async () => {
        if (!user)
            return;
        try {
            let startDate, endDate;
            if (viewMode === 'month') {
                // Get month boundaries in user's timezone, converted to UTC for database query
                const boundaries = getMonthBoundariesUtc(currentDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
            }
            else if (viewMode === 'week') {
                // Get week boundaries in user's timezone, converted to UTC
                const boundaries = getWeekBoundariesUtc(currentDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
            }
            else {
                // Day view - Use user timezone boundaries converted to UTC
                const boundaries = getDayBoundariesUtc(selectedDate, userTimezone);
                startDate = boundaries.start;
                endDate = boundaries.end;
            }
            console.log('⏱️ fetchLengths - Query dates:', startDate.toISOString(), 'to', endDate.toISOString(), 'for view mode:', viewMode);
            // Fetch one-time scheduled lengths
            const { data: oneTimeLengths, error: oneTimeError } = await supabase
                .from('lengths')
                .select('*')
                .eq('user_id', user.id)
                .not('scheduled_start_time', 'is', null)
                .is('frequency', null) // Non-recurring
                .gte('scheduled_start_time', startDate.toISOString())
                .lte('scheduled_start_time', endDate.toISOString())
                .order('scheduled_start_time', { ascending: true });
            if (oneTimeError)
                throw oneTimeError;
            console.log('⏱️ fetchLengths - One-time lengths fetched:', oneTimeLengths?.length || 0);
            // Fetch recurring lengths (with broader date range)
            const recurringStartDate = new Date(startDate);
            recurringStartDate.setMonth(recurringStartDate.getMonth() - 3);
            const { data: recurringLengths, error: recurringError } = await supabase
                .from('lengths')
                .select('*')
                .eq('user_id', user.id)
                .not('scheduled_start_time', 'is', null)
                .not('frequency', 'is', null) // Must have frequency
                .lte('scheduled_start_time', endDate.toISOString())
                .order('scheduled_start_time', { ascending: true });
            if (recurringError)
                throw recurringError;
            console.log('⏱️ fetchLengths - Recurring lengths fetched:', recurringLengths?.length || 0);
            // Generate recurring instances
            let allLengths = [...(oneTimeLengths || [])];
            if (recurringLengths && recurringLengths.length > 0) {
                recurringLengths.forEach(length => {
                    const instances = generateRecurringInstances(length, startDate, endDate);
                    allLengths = [...allLengths, ...instances];
                });
            }
            // Sort by start time
            allLengths.sort((a, b) => {
                const aTime = a.scheduled_start_time ? new Date(a.scheduled_start_time).getTime() : 0;
                const bTime = b.scheduled_start_time ? new Date(b.scheduled_start_time).getTime() : 0;
                return aTime - bTime;
            });
            console.log('⏱️ fetchLengths - Total lengths to set:', allLengths.length);
            setLengths(allLengths);
        }
        catch (error) {
            console.error('Error fetching lengths:', error);
        }
    };
    const openCreateModal = (date, type = 'step') => {
        const startDate = date || selectedDate;
        const startTime = createDateAtTimeInTimezone(startDate, 9, 0, userTimezone);
        const endTime = createDateAtTimeInTimezone(startDate, 10, 0, userTimezone);
        // Set mode based on type
        setIsTaskListMode(type === 'step_list');
        setIsLengthMode(type === 'length');
        // Set appropriate defaults based on type
        const defaultColor = type === 'step_list' ? '#3B82F6' : type === 'length' ? '#10B981' : '#EC4899';
        const defaultIcon = type === 'step_list' ? '📋' : type === 'length' ? '📏' : '📅';
        setEventForm({
            label: '',
            description: '',
            location: '',
            scheduled_start_time: startTime,
            scheduled_end_time: endTime,
            all_day: false,
            attendees: [],
            color: defaultColor,
            icon: defaultIcon,
            status: 'confirmed',
            isbuildhabit: false, // Default to activity
            duration: 60, // Default 60 minutes
            completed: false,
            // Mauna-app fields
            mantra: '',
            starting_ritual: '',
            ending_ritual: '',
            audio_url: null,
            audio_type: null,
            journal_content: '',
            cbt_notes: '',
            dddpt_discover: '',
            dddpt_define: '',
            dddpt_ideate: '',
            dddpt_prototype: '',
            dddpt_test: '',
            energy_level: 3,
            priority_letter: 'None',
            priority_rank: 0,
            locked: false,
            alerts: [],
            range_id: undefined,
            mountain_id: undefined,
            hill_id: undefined,
            terrain_id: undefined,
        });
        setSelectedEvent(null);
        setShowEventModal(true);
        setShowCreateDropdown(false); // Close dropdown after selection
    };
    const openEditModal = async (event) => {
        console.log('Opening edit modal for event:', event.id);
        // Check if this is a TaskList or Length
        const isTaskListEvent = isTaskList(event);
        const isLengthEvent = isLength(event);
        setIsTaskListMode(isTaskListEvent);
        setIsLengthMode(isLengthEvent);
        // Ensure all date fields are strings (ISO format) not Date objects
        const formattedEvent = {
            ...event,
            label: (isTaskListEvent || isLengthEvent) ? event.name : event.label,
            scheduled_start_time: event.scheduled_start_time
                ? (typeof event.scheduled_start_time === 'string'
                    ? event.scheduled_start_time
                    : new Date(event.scheduled_start_time).toISOString())
                : undefined,
            scheduled_end_time: event.scheduled_end_time
                ? (typeof event.scheduled_end_time === 'string'
                    ? event.scheduled_end_time
                    : new Date(event.scheduled_end_time).toISOString())
                : undefined,
        };
        setEventForm(formattedEvent);
        setSelectedEvent(event);
        // Fetch linked steps for task_lists and lengths, or breaths for regular steps
        if (event.id) {
            if (isTaskListEvent || isLengthEvent) {
                await fetchLinkedSteps(event.id);
            }
            else {
                await fetchBreaths(event.id);
            }
        }
        setShowEventModal(true);
    };
    const closeModal = () => {
        setShowEventModal(false);
        setSelectedEvent(null);
        setIsTaskListMode(false);
        setEventForm({
            label: '',
            description: '',
            location: '',
            scheduled_start_time: undefined,
            scheduled_end_time: undefined,
            all_day: false,
            attendees: [],
            color: '#3B82F6',
            status: 'confirmed',
            isbuildhabit: false,
            duration: 60,
            completed: false,
            // Mauna-app fields
            mantra: '',
            starting_ritual: '',
            ending_ritual: '',
            audio_url: null,
            audio_type: null,
            journal_content: '',
            cbt_notes: '',
            dddpt_discover: '',
            dddpt_define: '',
            dddpt_ideate: '',
            dddpt_prototype: '',
            dddpt_test: '',
            energy_level: 3,
            priority_letter: 'None',
            priority_rank: 0,
            locked: false,
            alerts: [],
            range_id: undefined,
            mountain_id: undefined,
            hill_id: undefined,
            terrain_id: undefined,
        });
        setBreaths([]);
        setNewBreathName('');
        setLinkedSteps([]);
        setNewStepName('');
    };
    // Breaths helper functions
    const handleAddBreath = () => {
        if (newBreathName.trim()) {
            setBreaths([
                ...breaths,
                {
                    name: newBreathName.trim(),
                    time_estimation_seconds: 300, // Default 5 minutes
                    position: breaths.length + 2, // Position must be > 1, so start from 2
                    completed: false
                }
            ]);
            setNewBreathName('');
        }
    };
    const handleRemoveBreath = (index) => {
        setBreaths(breaths.filter((_, i) => i !== index));
    };
    const handleToggleBreathComplete = (index) => {
        const updatedBreaths = [...breaths];
        updatedBreaths[index] = {
            ...updatedBreaths[index],
            completed: !updatedBreaths[index].completed
        };
        setBreaths(updatedBreaths);
    };
    const fetchBreaths = async (stepId) => {
        try {
            console.log('Fetching breaths for step:', stepId);
            const { data, error } = await supabase
                .from('breaths')
                .select('*')
                .eq('step_id', stepId)
                .order('position', { ascending: true });
            if (error) {
                console.error('Error fetching breaths:', error);
                throw error;
            }
            console.log('Fetched breaths:', data);
            setBreaths(data || []);
        }
        catch (error) {
            console.error('Error fetching breaths:', error);
            setBreaths([]);
        }
    };
    const saveBreaths = async (stepId) => {
        if (!user)
            return;
        try {
            console.log('Saving breaths for step:', stepId, 'Count:', breaths.length);
            // Delete existing breaths for this step
            const { error: deleteError } = await supabase
                .from('breaths')
                .delete()
                .eq('step_id', stepId);
            if (deleteError) {
                console.error('Error deleting existing breaths:', deleteError);
            }
            // Insert new breaths
            if (breaths.length > 0) {
                const breathsToInsert = breaths.map((breath, index) => ({
                    step_id: stepId,
                    user_id: user.id,
                    name: breath.name,
                    time_estimation_seconds: breath.time_estimation_seconds || 300,
                    position: index + 2, // Position must be > 1, so start from 2
                    completed: breath.completed || false,
                    is_running: false,
                    paused_time: 0,
                    total_time_seconds: 0
                }));
                console.log('Inserting breaths:', breathsToInsert);
                const { error } = await supabase
                    .from('breaths')
                    .insert(breathsToInsert);
                if (error) {
                    console.error('Error inserting breaths:', error);
                    throw error;
                }
                console.log('Breaths saved successfully');
            }
        }
        catch (error) {
            console.error('Error saving breaths:', error);
            throw error;
        }
    };
    // Functions for managing steps linked to task_lists
    const fetchLinkedSteps = async (parentId) => {
        try {
            console.log('Fetching linked steps for parent:', parentId);
            // Try to fetch steps linked by task_list_id or length_id
            const { data: taskListSteps, error: taskListError } = await supabase
                .from('steps')
                .select('*')
                .eq('task_list_id', parentId)
                .order('created_at', { ascending: true });
            const { data: lengthSteps, error: lengthError } = await supabase
                .from('steps')
                .select('*')
                .eq('length_id', parentId)
                .order('created_at', { ascending: true });
            if (taskListError && lengthError) {
                console.error('Error fetching linked steps:', taskListError || lengthError);
                throw taskListError || lengthError;
            }
            // Use whichever has results
            const data = (taskListSteps && taskListSteps.length > 0) ? taskListSteps : lengthSteps;
            console.log('Fetched linked steps:', data);
            setLinkedSteps(data || []);
        }
        catch (error) {
            console.error('Error fetching linked steps:', error);
            setLinkedSteps([]);
        }
    };
    const saveLinkedSteps = async (parentId) => {
        if (!user)
            return;
        try {
            console.log('Saving linked steps for parent:', parentId);
            // Determine if this is for a task_list or length based on mode
            const isForLength = isLengthMode;
            // Update existing steps that were modified
            for (const step of linkedSteps) {
                if (step.id) {
                    // Existing step - update it
                    const updateData = {
                        label: step.label,
                        duration: step.duration || 60,
                        updated_at: new Date().toISOString()
                    };
                    // Set the appropriate parent ID field
                    if (isForLength) {
                        updateData.length_id = parentId;
                        updateData.task_list_id = null;
                    }
                    else {
                        updateData.task_list_id = parentId;
                        updateData.length_id = null;
                    }
                    const { error } = await supabase
                        .from('steps')
                        .update(updateData)
                        .eq('id', step.id);
                    if (error) {
                        console.error('Error updating step:', error);
                        throw error;
                    }
                }
                else {
                    // New step - insert it
                    const insertData = {
                        user_id: user.id,
                        label: step.label,
                        duration: step.duration || 60,
                        isbuildhabit: false,
                        completed: false,
                        attendees: [],
                        color: isForLength ? '#10B981' : '#3B82F6',
                        status: 'pending',
                        all_day: false,
                        scheduled_start_time: null,
                        scheduled_end_time: null
                    };
                    // Set the appropriate parent ID field
                    if (isForLength) {
                        insertData.length_id = parentId;
                    }
                    else {
                        insertData.task_list_id = parentId;
                    }
                    const { error } = await supabase
                        .from('steps')
                        .insert(insertData);
                    if (error) {
                        console.error('Error inserting step:', error);
                        throw error;
                    }
                }
            }
            console.log('Linked steps saved successfully');
        }
        catch (error) {
            console.error('Error saving linked steps:', error);
            throw error;
        }
    };
    const handleAddLinkedStep = () => {
        if (newStepName.trim()) {
            setLinkedSteps([
                ...linkedSteps,
                {
                    label: newStepName.trim(),
                    duration: 60,
                    isbuildhabit: false,
                    completed: false,
                    attendees: [],
                    user_id: user?.id || '',
                    color: '#3B82F6',
                    status: 'pending',
                    all_day: false,
                    scheduled_start_time: null,
                    scheduled_end_time: null
                }
            ]);
            setNewStepName('');
        }
    };
    const handleRemoveLinkedStep = (index) => {
        setLinkedSteps(linkedSteps.filter((_, i) => i !== index));
    };
    const fetchBreathCounts = async (eventIds) => {
        if (!user || eventIds.length === 0)
            return;
        try {
            console.log('Fetching breath counts for events:', eventIds);
            const { data, error } = await supabase
                .from('breaths')
                .select('step_id, completed')
                .in('step_id', eventIds);
            if (error) {
                console.error('Error fetching breath counts:', error);
                return;
            }
            // Calculate counts per event
            const counts = new Map();
            eventIds.forEach(id => {
                const eventBreaths = data?.filter(b => b.step_id === id) || [];
                const total = eventBreaths.length;
                const completed = eventBreaths.filter(b => b.completed).length;
                counts.set(id, { total, completed });
            });
            console.log('Breath counts:', Object.fromEntries(counts));
            setBreathCounts(counts);
        }
        catch (error) {
            console.error('Error fetching breath counts:', error);
        }
    };
    // Event Type Conversion Handler
    const handleConvertEventType = async (targetType) => {
        if (!user || !selectedEvent?.id)
            return;
        const currentType = isTaskListMode ? 'task_list' : isLengthMode ? 'length' : 'step';
        if (currentType === targetType) {
            alert('Event is already of this type');
            return;
        }
        const confirmMessage = `Convert this ${currentType === 'step' ? 'Step (Activity)' : currentType === 'task_list' ? 'Task List' : 'Length'} to ${targetType === 'step' ? 'Step (Activity)' : targetType === 'task_list' ? 'Task List' : 'Length'}?\n\n${currentType === 'step' && (targetType === 'task_list' || targetType === 'length')
            ? `${breaths.length} connected breaths will be converted to steps.`
            : (currentType === 'task_list' || currentType === 'length') && targetType === 'step'
                ? `${linkedSteps.length} connected steps will be converted to breaths.`
                : 'Connected items will be preserved.'}`;
        if (!confirm(confirmMessage))
            return;
        setLoading(true);
        try {
            const eventId = selectedEvent.id;
            // Step 1: Convert the parent event type
            if (currentType === 'step' && (targetType === 'task_list' || targetType === 'length')) {
                // Converting step to task_list or length
                const stepData = selectedEvent;
                const newParentData = {
                    user_id: user.id,
                    name: stepData.label,
                    scheduled_start_time: stepData.scheduled_start_time,
                    scheduled_end_time: stepData.scheduled_end_time,
                    all_day: stepData.all_day,
                    duration: stepData.duration,
                    color: stepData.color || '#3B82F6',
                    icon: stepData.icon || (targetType === 'task_list' ? '📋' : '⏱️'),
                    status: stepData.status || 'confirmed',
                    frequency: stepData.frequency || null,
                    recurrence_rule: stepData.recurrence_rule || null,
                    recurrence_exception_dates: stepData.recurrence_exception_dates || null,
                    isbuildhabit: false,
                    timezone: userTimezone
                };
                if (targetType === 'length') {
                    newParentData.terrain_id = stepData.terrain_id || null;
                    newParentData.description = stepData.description || '';
                    newParentData.completed = stepData.completed || false;
                }
                // Insert new parent (task_list or length)
                const { data: newParent, error: insertError } = await supabase
                    .from(targetType === 'task_list' ? 'task_lists' : 'lengths')
                    .insert(newParentData)
                    .select()
                    .single();
                if (insertError)
                    throw insertError;
                // Step 2: Convert breaths to steps
                if (breaths.length > 0) {
                    const stepsToInsert = breaths.map((breath, index) => ({
                        user_id: user.id,
                        label: breath.name,
                        duration: Math.ceil((breath.time_estimation_seconds || 300) / 60), // Convert seconds to minutes
                        isbuildhabit: false,
                        completed: breath.completed || false,
                        attendees: [],
                        color: stepData.color || '#3B82F6',
                        status: 'pending',
                        all_day: false,
                        scheduled_start_time: null,
                        scheduled_end_time: null,
                        ...(targetType === 'task_list' ? { task_list_id: newParent.id } : { length_id: newParent.id })
                    }));
                    const { error: stepsError } = await supabase
                        .from('steps')
                        .insert(stepsToInsert);
                    if (stepsError)
                        throw stepsError;
                }
                // Step 3: Delete old breaths and step
                if (breaths.length > 0) {
                    await supabase.from('breaths').delete().eq('step_id', eventId);
                }
                await supabase.from('steps').delete().eq('id', eventId);
                alert(`Successfully converted to ${targetType === 'task_list' ? 'Task List' : 'Length'} with ${breaths.length} steps!`);
            }
            else if ((currentType === 'task_list' || currentType === 'length') && targetType === 'step') {
                // Converting task_list or length to step
                const parentData = selectedEvent;
                const newStepData = {
                    user_id: user.id,
                    label: parentData.name,
                    scheduled_start_time: parentData.scheduled_start_time,
                    scheduled_end_time: parentData.scheduled_end_time,
                    all_day: parentData.all_day,
                    duration: parentData.duration,
                    color: parentData.color || '#3B82F6',
                    icon: parentData.icon || '✓',
                    status: parentData.status || 'confirmed',
                    frequency: parentData.frequency || null,
                    recurrence_rule: parentData.recurrence_rule || null,
                    recurrence_exception_dates: parentData.recurrence_exception_dates || null,
                    isbuildhabit: false,
                    completed: false,
                    attendees: [],
                    timezone: userTimezone,
                    description: parentData.description || '',
                    terrain_id: parentData.terrain_id || null
                };
                // Insert new step
                const { data: newStep, error: insertError } = await supabase
                    .from('steps')
                    .insert(newStepData)
                    .select()
                    .single();
                if (insertError)
                    throw insertError;
                // Step 2: Convert steps to breaths
                if (linkedSteps.length > 0) {
                    const breathsToInsert = linkedSteps.map((step, index) => ({
                        step_id: newStep.id,
                        user_id: user.id,
                        name: step.label,
                        time_estimation_seconds: step.duration * 60, // Convert minutes to seconds
                        position: index + 2,
                        completed: step.completed || false,
                        is_running: false,
                        paused_time: 0,
                        total_time_seconds: 0
                    }));
                    const { error: breathsError } = await supabase
                        .from('breaths')
                        .insert(breathsToInsert);
                    if (breathsError)
                        throw breathsError;
                }
                // Step 3: Delete old steps and parent
                if (linkedSteps.length > 0) {
                    await supabase.from('steps').delete().or(`task_list_id.eq.${eventId},length_id.eq.${eventId}`);
                }
                await supabase.from(currentType === 'task_list' ? 'task_lists' : 'lengths').delete().eq('id', eventId);
                alert(`Successfully converted to Step (Activity) with ${linkedSteps.length} breaths!`);
            }
            else if (currentType === 'task_list' && targetType === 'length') {
                // Converting task_list to length
                const taskListData = selectedEvent;
                const newLengthData = {
                    user_id: user.id,
                    name: taskListData.name,
                    scheduled_start_time: taskListData.scheduled_start_time,
                    scheduled_end_time: taskListData.scheduled_end_time,
                    all_day: taskListData.all_day,
                    duration: taskListData.duration,
                    color: taskListData.color || '#10B981',
                    icon: taskListData.icon || '⏱️',
                    status: taskListData.status || 'confirmed',
                    frequency: taskListData.frequency || null,
                    recurrence_rule: taskListData.recurrence_rule || null,
                    recurrence_exception_dates: taskListData.recurrence_exception_dates || null,
                    isbuildhabit: false,
                    completed: false,
                    timezone: userTimezone,
                    description: '',
                    terrain_id: null
                };
                // Insert new length
                const { data: newLength, error: insertError } = await supabase
                    .from('lengths')
                    .insert(newLengthData)
                    .select()
                    .single();
                if (insertError)
                    throw insertError;
                // Update steps to point to new length
                if (linkedSteps.length > 0) {
                    const { error: updateError } = await supabase
                        .from('steps')
                        .update({ length_id: newLength.id, task_list_id: null })
                        .eq('task_list_id', eventId);
                    if (updateError)
                        throw updateError;
                }
                // Delete old task_list
                await supabase.from('task_lists').delete().eq('id', eventId);
                alert(`Successfully converted to Length with ${linkedSteps.length} steps!`);
            }
            else if (currentType === 'length' && targetType === 'task_list') {
                // Converting length to task_list
                const lengthData = selectedEvent;
                const newTaskListData = {
                    user_id: user.id,
                    name: lengthData.name,
                    scheduled_start_time: lengthData.scheduled_start_time,
                    scheduled_end_time: lengthData.scheduled_end_time,
                    all_day: lengthData.all_day,
                    duration: lengthData.duration,
                    color: lengthData.color || '#3B82F6',
                    icon: lengthData.icon || '📋',
                    status: lengthData.status || 'confirmed',
                    frequency: lengthData.frequency || null,
                    recurrence_rule: lengthData.recurrence_rule || null,
                    recurrence_exception_dates: lengthData.recurrence_exception_dates || null,
                    isbuildhabit: false,
                    timezone: userTimezone
                };
                // Insert new task_list
                const { data: newTaskList, error: insertError } = await supabase
                    .from('task_lists')
                    .insert(newTaskListData)
                    .select()
                    .single();
                if (insertError)
                    throw insertError;
                // Update steps to point to new task_list
                if (linkedSteps.length > 0) {
                    const { error: updateError } = await supabase
                        .from('steps')
                        .update({ task_list_id: newTaskList.id, length_id: null })
                        .eq('length_id', eventId);
                    if (updateError)
                        throw updateError;
                }
                // Delete old length
                await supabase.from('lengths').delete().eq('id', eventId);
                alert(`Successfully converted to Task List with ${linkedSteps.length} steps!`);
            }
            // Refresh the calendar
            await fetchEvents();
            await fetchTaskLists();
            await fetchLengths();
            // Close modal
            closeModal();
        }
        catch (error) {
            console.error('Error converting event type:', error);
            alert('Failed to convert event type. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const saveEvent = async () => {
        if (!user || !eventForm.label || !eventForm.scheduled_start_time || !eventForm.scheduled_end_time)
            return;
        // If editing a recurring event, show update scope modal instead
        const isRecurringEvent = selectedEvent && (eventForm.frequency || eventForm.isbuildhabit) && (eventForm.frequency?.toLowerCase() !== 'none');
        if (isRecurringEvent && selectedEvent?.id) {
            setShowUpdateScopeModal(true);
            return;
        }
        await performSaveEvent();
    };
    const performSaveEvent = async () => {
        await performSaveEventWithScope(updateScope);
    };
    const performSaveEventWithScope = async (scope) => {
        if (!user || !eventForm.label || !eventForm.scheduled_start_time || !eventForm.scheduled_end_time)
            return;
        console.log('💾 performSaveEventWithScope called with scope:', scope);
        console.log('📝 Event details:', {
            label: eventForm.label,
            start: new Date(eventForm.scheduled_start_time).toLocaleTimeString(),
            end: new Date(eventForm.scheduled_end_time).toLocaleTimeString(),
            isTaskListMode,
            isLengthMode
        });
        setLoading(true);
        try {
            // Calculate duration in minutes
            const startTime = new Date(eventForm.scheduled_start_time);
            const endTime = new Date(eventForm.scheduled_end_time);
            const durationMinutes = Math.ceil((endTime.getTime() - startTime.getTime()) / (1000 * 60));
            if (isTaskListMode) {
                // Save as TaskList
                let taskListId;
                const taskListData = {
                    name: eventForm.label,
                    isbuildhabit: eventForm.isbuildhabit || false,
                    scheduled_start_time: eventForm.scheduled_start_time,
                    scheduled_end_time: eventForm.scheduled_end_time,
                    all_day: eventForm.all_day || false,
                    frequency: eventForm.frequency || null,
                    recurrence_rule: eventForm.recurrence_rule || null,
                    recurrence_exception_dates: eventForm.recurrence_exception_dates || null,
                    color: eventForm.color || '#3B82F6',
                    icon: eventForm.icon || '📋',
                    status: eventForm.status || 'confirmed',
                    duration: durationMinutes,
                    timezone: userTimezone,
                    user_id: user.id
                };
                if (selectedEvent?.id && isTaskList(selectedEvent)) {
                    // Update existing task_list
                    // Check if this is a task_list instance (composite ID like taskListId-YYYY-MM-DD)
                    const isTaskListInstance = selectedEvent.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && eventForm.isbuildhabit;
                    const actualTaskListId = extractActualId(selectedEvent.id, isTaskListInstance);
                    // Handle update scope for recurring events
                    if (scope === 'this' && isTaskListInstance) {
                        // For editing a single instance: add date to exceptions and create new one-time event
                        const dateStr = selectedEvent.id.split('-').slice(-3).join('-');
                        // Update the recurring task_list to skip this date
                        const existingExceptions = eventForm.recurrence_exception_dates || [];
                        if (!existingExceptions.includes(dateStr)) {
                            existingExceptions.push(dateStr);
                        }
                        const { error: exceptionError } = await supabase
                            .from('task_lists')
                            .update({
                            recurrence_exception_dates: existingExceptions,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualTaskListId);
                        if (exceptionError)
                            throw exceptionError;
                        // Create a new one-time task_list with the modified details
                        const oneTimeEventData = {
                            ...taskListData,
                            isbuildhabit: false, // Make it non-recurring
                            frequency: null,
                            recurrence_rule: null,
                            recurrence_exception_dates: null
                        };
                        const { data: newEvent, error: createError } = await supabase
                            .from('task_lists')
                            .insert(oneTimeEventData)
                            .select()
                            .single();
                        if (createError)
                            throw createError;
                        taskListId = newEvent.id;
                        console.log('✅ Created new one-time task_list:', taskListId, 'and added', dateStr, 'to exceptions');
                    }
                    else if (scope === 'future') {
                        // For "Update this and future events", create a new recurring task_list starting from today
                        // and add today's date to the original task_list's exceptions
                        const dateStr = selectedEvent.id.split('-').slice(-3).join('-');
                        const existingExceptions = eventForm.recurrence_exception_dates || [];
                        if (!existingExceptions.includes(dateStr)) {
                            existingExceptions.push(dateStr);
                        }
                        // Update the original recurring task_list to skip this date
                        const { error: exceptionError } = await supabase
                            .from('task_lists')
                            .update({
                            recurrence_exception_dates: existingExceptions,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualTaskListId);
                        if (exceptionError)
                            throw exceptionError;
                        // Create a NEW recurring task_list starting from today with the new changes
                        const newRecurringData = {
                            ...taskListData,
                            id: undefined // Let database generate new ID
                        };
                        const { data: newEvent, error: createError } = await supabase
                            .from('task_lists')
                            .insert(newRecurringData)
                            .select()
                            .single();
                        if (createError)
                            throw createError;
                        taskListId = newEvent.id;
                        console.log('✅ Created new recurring task_list from', dateStr, 'and added to exceptions:', taskListId);
                    }
                    else {
                        // Update all instances of this recurring task_list
                        const { error } = await supabase
                            .from('task_lists')
                            .update({
                            ...taskListData,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualTaskListId);
                        if (error)
                            throw error;
                        taskListId = actualTaskListId;
                    }
                }
                else {
                    // Create new task_list
                    const { data, error } = await supabase
                        .from('task_lists')
                        .insert(taskListData)
                        .select()
                        .single();
                    if (error)
                        throw error;
                    taskListId = data.id;
                }
                // Save linked steps for this task_list
                await saveLinkedSteps(taskListId);
                await fetchTaskLists();
            }
            else if (isLengthMode) {
                // Save as Length
                let lengthId;
                const lengthData = {
                    name: eventForm.label,
                    description: eventForm.description || null,
                    scheduled_start_time: eventForm.scheduled_start_time,
                    scheduled_end_time: eventForm.scheduled_end_time,
                    all_day: eventForm.all_day || false,
                    frequency: eventForm.frequency || null,
                    recurrence_rule: eventForm.recurrence_rule || null,
                    recurrence_exception_dates: eventForm.recurrence_exception_dates || null,
                    color: eventForm.color || '#10B981',
                    icon: eventForm.icon || '📏',
                    status: eventForm.status || 'confirmed',
                    duration: durationMinutes,
                    isbuildhabit: eventForm.isbuildhabit || false,
                    completed: eventForm.completed || false,
                    timezone: userTimezone,
                    user_id: user.id
                };
                if (selectedEvent?.id && isLength(selectedEvent)) {
                    // Update existing length
                    // Check if this is a length instance (composite ID like lengthId-YYYY-MM-DD)
                    const isLengthInstance = selectedEvent.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && eventForm.isbuildhabit;
                    const actualLengthId = extractActualId(selectedEvent.id, isLengthInstance);
                    // Handle update scope for recurring events
                    if (scope === 'this' && isLengthInstance) {
                        // For editing a single instance: add date to exceptions and create new one-time event
                        const dateStr = selectedEvent.id.split('-').slice(-3).join('-');
                        // Update the recurring length to skip this date
                        const existingExceptions = eventForm.recurrence_exception_dates || [];
                        if (!existingExceptions.includes(dateStr)) {
                            existingExceptions.push(dateStr);
                        }
                        const { error: exceptionError } = await supabase
                            .from('lengths')
                            .update({
                            recurrence_exception_dates: existingExceptions,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualLengthId);
                        if (exceptionError)
                            throw exceptionError;
                        // Create a new one-time length with the modified details
                        const oneTimeEventData = {
                            ...lengthData,
                            isbuildhabit: false, // Make it non-recurring
                            frequency: null,
                            recurrence_rule: null,
                            recurrence_exception_dates: null
                        };
                        const { data: newEvent, error: createError } = await supabase
                            .from('lengths')
                            .insert(oneTimeEventData)
                            .select()
                            .single();
                        if (createError)
                            throw createError;
                        lengthId = newEvent.id;
                        console.log('✅ Created new one-time length:', lengthId, 'and added', dateStr, 'to exceptions');
                    }
                    else if (scope === 'future') {
                        // For "Update this and future events", create a new recurring length starting from today
                        // and add today's date to the original length's exceptions
                        const dateStr = selectedEvent.id.split('-').slice(-3).join('-');
                        const existingExceptions = eventForm.recurrence_exception_dates || [];
                        if (!existingExceptions.includes(dateStr)) {
                            existingExceptions.push(dateStr);
                        }
                        // Update the original recurring length to skip this date
                        const { error: exceptionError } = await supabase
                            .from('lengths')
                            .update({
                            recurrence_exception_dates: existingExceptions,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualLengthId);
                        if (exceptionError)
                            throw exceptionError;
                        // Create a NEW recurring length starting from today with the new changes
                        const newRecurringData = {
                            ...lengthData,
                            id: undefined // Let database generate new ID
                        };
                        const { data: newEvent, error: createError } = await supabase
                            .from('lengths')
                            .insert(newRecurringData)
                            .select()
                            .single();
                        if (createError)
                            throw createError;
                        lengthId = newEvent.id;
                        console.log('✅ Created new recurring length from', dateStr, 'and added to exceptions:', lengthId);
                    }
                    else {
                        // Update all instances of this recurring length
                        const { error } = await supabase
                            .from('lengths')
                            .update({
                            ...lengthData,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualLengthId);
                        if (error)
                            throw error;
                        lengthId = actualLengthId;
                    }
                }
                else {
                    // Create new length
                    const { data, error } = await supabase
                        .from('lengths')
                        .insert(lengthData)
                        .select()
                        .single();
                    if (error)
                        throw error;
                    lengthId = data.id;
                }
                // Save linked steps for this length
                await saveLinkedSteps(lengthId);
                await fetchLengths();
            }
            else {
                // Save as Step (activity/event)
                let stepId;
                const stepData = {
                    label: eventForm.label,
                    description: eventForm.description || null,
                    location: eventForm.location || null,
                    scheduled_start_time: eventForm.scheduled_start_time,
                    scheduled_end_time: eventForm.scheduled_end_time,
                    all_day: eventForm.all_day || false,
                    frequency: eventForm.frequency || null,
                    attendees: eventForm.attendees || [],
                    color: eventForm.color || '#EC4899',
                    icon: eventForm.icon || '📅',
                    habit_notes: eventForm.habit_notes || null,
                    isbuildhabit: eventForm.isbuildhabit || false,
                    status: eventForm.status || 'confirmed',
                    duration: durationMinutes,
                    completed: eventForm.completed || false,
                    locked: eventForm.locked || false,
                    mantra: eventForm.mantra || null,
                    starting_ritual: eventForm.starting_ritual || null,
                    ending_ritual: eventForm.ending_ritual || null,
                    audio_url: eventForm.audio_url || null,
                    audio_type: eventForm.audio_type || null,
                    priority_letter: eventForm.priority_letter || null,
                    priority_rank: eventForm.priority_rank || null,
                    alerts: eventForm.alerts || null,
                    timezone: userTimezone,
                    user_id: user.id
                };
                console.log('💾 Saving step with isbuildhabit:', stepData.isbuildhabit, 'frequency:', stepData.frequency);
                // Only add optional fields if they have values to avoid schema cache issues
                if (eventForm.cbt_notes)
                    stepData.cbt_notes = eventForm.cbt_notes;
                if (eventForm.dddpt_discover)
                    stepData.dddpt_discover = eventForm.dddpt_discover;
                if (eventForm.dddpt_define)
                    stepData.dddpt_define = eventForm.dddpt_define;
                if (eventForm.dddpt_ideate)
                    stepData.dddpt_ideate = eventForm.dddpt_ideate;
                if (eventForm.dddpt_prototype)
                    stepData.dddpt_prototype = eventForm.dddpt_prototype;
                if (eventForm.dddpt_test)
                    stepData.dddpt_test = eventForm.dddpt_test;
                if (eventForm.energy_level)
                    stepData.energy_level = eventForm.energy_level;
                if (eventForm.range_id)
                    stepData.range_id = eventForm.range_id;
                if (eventForm.mountain_id)
                    stepData.mountain_id = eventForm.mountain_id;
                if (eventForm.hill_id)
                    stepData.hill_id = eventForm.hill_id;
                if (eventForm.terrain_id)
                    stepData.terrain_id = eventForm.terrain_id;
                if (selectedEvent?.id && !isTaskList(selectedEvent)) {
                    // Update existing step
                    // Check if this is a habit instance (composite ID like habitId-YYYY-MM-DD)
                    const isHabitInstance = selectedEvent.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && eventForm.isbuildhabit;
                    const actualStepId = extractActualId(selectedEvent.id, isHabitInstance);
                    // Handle update scope for recurring events
                    if (scope === 'this' && isHabitInstance) {
                        // For editing a single instance: add date to exceptions and create new one-time event
                        const dateStr = selectedEvent.id.split('-').slice(-3).join('-');
                        // Update the recurring event to skip this date
                        const existingExceptions = eventForm.recurrence_exception_dates || [];
                        if (!existingExceptions.includes(dateStr)) {
                            existingExceptions.push(dateStr);
                        }
                        const { error: exceptionError } = await supabase
                            .from('steps')
                            .update({
                            recurrence_exception_dates: existingExceptions,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualStepId);
                        if (exceptionError)
                            throw exceptionError;
                        // Create a new one-time event with the modified details
                        const oneTimeEventData = {
                            ...stepData,
                            isbuildhabit: false, // Make it non-recurring
                            frequency: null,
                            recurrence_rule: null,
                            recurrence_exception_dates: null
                        };
                        const { data: newEvent, error: createError } = await supabase
                            .from('steps')
                            .insert(oneTimeEventData)
                            .select()
                            .single();
                        if (createError)
                            throw createError;
                        stepId = newEvent.id;
                        console.log('✅ Created new one-time instance:', stepId, 'and added', dateStr, 'to exceptions');
                    }
                    else if (scope === 'future') {
                        // For "Update this and future events", create a new recurring event starting from today
                        // and add today's date to the original event's exceptions
                        const dateStr = selectedEvent.id.split('-').slice(-3).join('-');
                        const existingExceptions = eventForm.recurrence_exception_dates || [];
                        if (!existingExceptions.includes(dateStr)) {
                            existingExceptions.push(dateStr);
                        }
                        // Update the original recurring event to skip this date
                        const { error: exceptionError } = await supabase
                            .from('steps')
                            .update({
                            recurrence_exception_dates: existingExceptions,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualStepId);
                        if (exceptionError)
                            throw exceptionError;
                        // Create a NEW recurring event starting from today with the new changes
                        const newRecurringData = {
                            ...stepData,
                            id: undefined // Let database generate new ID
                        };
                        const { data: newEvent, error: createError } = await supabase
                            .from('steps')
                            .insert(newRecurringData)
                            .select()
                            .single();
                        if (createError)
                            throw createError;
                        stepId = newEvent.id;
                        console.log('✅ Created new recurring event from', dateStr, 'and added to exceptions:', stepId);
                    }
                    else {
                        // Update all instances of this recurring event
                        console.log('💾 Updating existing step:', actualStepId, 'with data:', stepData);
                        const { error } = await supabase
                            .from('steps')
                            .update({
                            ...stepData,
                            updated_at: new Date().toISOString()
                        })
                            .eq('id', actualStepId);
                        if (error) {
                            console.error('❌ Update error:', error);
                            throw error;
                        }
                        console.log('✅ Step updated successfully:', actualStepId);
                        stepId = actualStepId;
                    }
                }
                else {
                    // Create new step (activity/event)
                    const { data, error } = await supabase
                        .from('steps')
                        .insert(stepData)
                        .select()
                        .single();
                    if (error)
                        throw error;
                    stepId = data.id;
                }
                // Save breaths for this step
                await saveBreaths(stepId);
                // Refresh breath counts for this specific event
                await fetchBreathCounts([stepId]);
            }
            // Update selectedDate to the date of the newly created/updated event so fetches get the right data
            if (eventForm.scheduled_start_time) {
                const eventDate = new Date(eventForm.scheduled_start_time);
                setSelectedDate(eventDate);
            }
            console.log('✅ Save successful, calling fetchEvents()...');
            await fetchEvents();
            console.log('✅ fetchEvents() completed');
            await fetchUnscheduledSteps();
            console.log('✅ All data fetched, closing modal');
            closeModal();
        }
        catch (error) {
            console.error('Error saving event:', error);
            alert('Failed to save event. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const toggleHabitCompletion = async (event, e) => {
        if (e) {
            e.stopPropagation();
        }
        if (!user)
            return;
        // Check if this is a habit instance (composite ID format: habitId-dateStr)
        const isHabitInstance = event.id && event.id.includes('-') && event.isbuildhabit;
        if (!isHabitInstance)
            return;
        try {
            // Extract the original habit ID and date from composite ID
            const parts = event.id.split('-');
            // The date part is the last 3 parts (YYYY-MM-DD)
            const dateStr = parts.slice(-3).join('-');
            const habitId = parts.slice(0, -3).join('-');
            // Fetch the current habit data
            const { data: habitData, error: fetchError } = await supabase
                .from('steps')
                .select('history, habit_skipped')
                .eq('id', habitId)
                .single();
            if (fetchError)
                throw fetchError;
            const currentHistory = habitData.history || [];
            const currentSkipped = habitData.habit_skipped || {};
            // Apply three-state toggle logic (same as HabitDetailModal)
            const isCompleted = currentHistory.includes(dateStr);
            const isSkipped = currentSkipped[dateStr];
            let updatedHistory = [...currentHistory];
            let updatedSkipped = { ...currentSkipped };
            if (isCompleted) {
                // completed → skipped
                const idx = updatedHistory.indexOf(dateStr);
                updatedHistory.splice(idx, 1);
                updatedSkipped[dateStr] = true;
            }
            else if (isSkipped) {
                // skipped → incomplete
                delete updatedSkipped[dateStr];
            }
            else {
                // incomplete → completed
                updatedHistory.push(dateStr);
                delete updatedSkipped[dateStr];
            }
            // Update the database
            const { error: updateError } = await supabase
                .from('steps')
                .update({
                history: updatedHistory,
                habit_skipped: updatedSkipped,
                updated_at: new Date().toISOString()
            })
                .eq('id', habitId);
            if (updateError)
                throw updateError;
            // Refresh events to show updated status
            await fetchEvents();
        }
        catch (error) {
            console.error('Error toggling habit completion:', error);
            alert('Failed to update habit. Please try again.');
        }
    };
    const initiateDelete = (event) => {
        // Check if this is a recurring instance
        if (event._isRecurringInstance) {
            // Show delete options modal for recurring events
            setEventToDelete(event);
            setShowDeleteModal(true);
        }
        else {
            // For non-recurring events, delete directly with confirmation
            if (confirm('Are you sure you want to delete this event?')) {
                deleteEventConfirmed(event.id, 'single');
            }
        }
    };
    const deleteEventConfirmed = async (eventId, deleteType) => {
        setLoading(true);
        try {
            if (deleteType === 'all-instances') {
                // Delete the base recurring habit from database
                const { error } = await supabase
                    .from('steps')
                    .delete()
                    .eq('id', eventId);
                if (error)
                    throw error;
            }
            else if (deleteType === 'this-instance') {
                // Add this instance date to the recurrence_exception_dates array
                if (!eventToDelete) {
                    throw new Error('No event to delete');
                }
                // Get the instance date from the event
                const instanceDate = eventToDelete._instanceDate || eventToDelete.scheduled_start_time;
                if (!instanceDate) {
                    throw new Error('Could not determine instance date');
                }
                // Fetch the current event to get existing exception dates
                const { data: currentEvent, error: fetchError } = await supabase
                    .from('steps')
                    .select('recurrence_exception_dates')
                    .eq('id', eventId)
                    .single();
                if (fetchError)
                    throw fetchError;
                // Add the new exception date to the array
                const existingExceptions = currentEvent?.recurrence_exception_dates || [];
                const updatedExceptions = [...existingExceptions, instanceDate];
                // Update the event with the new exception dates
                const { error: updateError } = await supabase
                    .from('steps')
                    .update({
                    recurrence_exception_dates: updatedExceptions,
                    updated_at: new Date().toISOString()
                })
                    .eq('id', eventId);
                if (updateError)
                    throw updateError;
            }
            else {
                // Single non-recurring event
                const { error } = await supabase
                    .from('steps')
                    .delete()
                    .eq('id', eventId);
                if (error)
                    throw error;
            }
            await fetchEvents();
            await fetchUnscheduledSteps();
            closeModal();
            setShowDeleteModal(false);
            setEventToDelete(null);
        }
        catch (error) {
            console.error('Error deleting event:', error);
            alert('Failed to delete event. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const unscheduleEvent = async () => {
        if (!selectedEvent?.id)
            return;
        setLoading(true);
        try {
            // Determine if this is a habit instance
            const isHabitInstance = selectedEvent.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && selectedEvent.isbuildhabit;
            const actualId = extractActualId(selectedEvent.id, isHabitInstance);
            // Update the event to remove scheduled times
            const { error } = await supabase
                .from('steps')
                .update({
                scheduled_start_time: null,
                scheduled_end_time: null,
                updated_at: new Date().toISOString()
            })
                .eq('id', actualId);
            if (error)
                throw error;
            await fetchEvents();
            await fetchUnscheduledSteps();
            closeModal();
        }
        catch (error) {
            console.error('Error unscheduling event:', error);
            alert('Failed to unschedule event. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    // Quick Edit Modal Functions for Inbox
    const openQuickEditModal = (step) => {
        setQuickEditStep(step);
        setShowQuickEditModal(true);
    };
    const closeQuickEditModal = () => {
        setShowQuickEditModal(false);
        setQuickEditStep(null);
    };
    const saveQuickEdit = async () => {
        if (!user || !quickEditStep)
            return;
        setLoading(true);
        try {
            const updates = {
                label: quickEditStep.label,
                description: quickEditStep.description,
                color: quickEditStep.color,
                isbuildhabit: quickEditStep.isbuildhabit,
                duration: quickEditStep.duration,
                updated_at: new Date().toISOString()
            };
            // If scheduled_start_time is set, also set scheduled_end_time
            if (quickEditStep.scheduled_start_time) {
                updates.scheduled_start_time = quickEditStep.scheduled_start_time;
                const startTime = new Date(quickEditStep.scheduled_start_time);
                const endTime = new Date(startTime.getTime() + (quickEditStep.duration || 60) * 60 * 1000);
                updates.scheduled_end_time = endTime.toISOString();
            }
            const { error } = await supabase
                .from('steps')
                .update(updates)
                .eq('id', quickEditStep.id);
            if (error)
                throw error;
            await fetchEvents();
            await fetchUnscheduledSteps();
            closeQuickEditModal();
        }
        catch (error) {
            console.error('Error saving quick edit:', error);
            alert('Failed to save changes. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const quickScheduleStep = async (step, date) => {
        if (!user)
            return;
        setLoading(true);
        try {
            const scheduleDate = date || selectedDate;
            const startTime = new Date(scheduleDate);
            startTime.setHours(9, 0, 0, 0); // Default to 9 AM
            const endTime = new Date(startTime.getTime() + (step.duration || 60) * 60 * 1000);
            const { error } = await supabase
                .from('steps')
                .update({
                scheduled_start_time: startTime.toISOString(),
                scheduled_end_time: endTime.toISOString(),
                updated_at: new Date().toISOString()
            })
                .eq('id', step.id);
            if (error)
                throw error;
            await fetchEvents();
            await fetchUnscheduledSteps();
        }
        catch (error) {
            console.error('Error scheduling step:', error);
            alert('Failed to schedule. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const deleteInboxStep = async (stepId) => {
        if (!confirm('Are you sure you want to delete this task?'))
            return;
        setLoading(true);
        try {
            const { error } = await supabase
                .from('steps')
                .delete()
                .eq('id', stepId);
            if (error)
                throw error;
            await fetchUnscheduledSteps();
        }
        catch (error) {
            console.error('Error deleting step:', error);
            alert('Failed to delete. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    // List View Functions
    const openRescheduleModal = (event) => {
        setRescheduleEvent(event);
        setShowRescheduleModal(true);
    };
    const closeRescheduleModal = () => {
        setShowRescheduleModal(false);
        setRescheduleEvent(null);
    };
    const saveReschedule = async () => {
        if (!user || !rescheduleEvent || !rescheduleEvent.scheduled_start_time)
            return;
        setLoading(true);
        try {
            const startTime = new Date(rescheduleEvent.scheduled_start_time);
            const endTime = new Date(startTime.getTime() + (rescheduleEvent.duration || 60) * 60 * 1000);
            const { error } = await supabase
                .from('steps')
                .update({
                scheduled_start_time: startTime.toISOString(),
                scheduled_end_time: endTime.toISOString(),
                updated_at: new Date().toISOString()
            })
                .eq('id', rescheduleEvent.id);
            if (error)
                throw error;
            await fetchEvents();
            closeRescheduleModal();
        }
        catch (error) {
            console.error('Error rescheduling event:', error);
            alert('Failed to reschedule. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const toggleEventType = async (event) => {
        if (!user)
            return;
        setLoading(true);
        try {
            const { error } = await supabase
                .from('steps')
                .update({
                isbuildhabit: !event.isbuildhabit,
                updated_at: new Date().toISOString()
            })
                .eq('id', event.id);
            if (error)
                throw error;
            await fetchEvents();
        }
        catch (error) {
            console.error('Error toggling event type:', error);
            alert('Failed to update. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    // Helper: Check if event spills over to next day(s)
    const getEventSpilloverDays = (event) => {
        if (!event.scheduled_start_time || !event.scheduled_end_time)
            return 0;
        const start = new Date(event.scheduled_start_time);
        const end = new Date(event.scheduled_end_time);
        // Calculate number of days the event spans
        const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysDiff;
    };
    const getEventsForDay = (day) => {
        const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        return events.filter(event => {
            if (!event.scheduled_start_time)
                return false;
            // Get the start date of the event (without time)
            const eventStartDate = new Date(event.scheduled_start_time);
            const eventStartDay = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
            // Event belongs to this day if it STARTS on this day
            // (We keep multi-day events on their starting day)
            return (eventStartDay.getDate() === dayDate.getDate() &&
                eventStartDay.getMonth() === dayDate.getMonth() &&
                eventStartDay.getFullYear() === dayDate.getFullYear());
        });
    };
    const getEventsForSelectedDate = () => {
        const allEvents = getAllCalendarEvents();
        const { start: dayStart, end: dayEnd } = getDayBoundariesUtc(selectedDate, userTimezone);
        return allEvents.filter(event => {
            if (!event.scheduled_start_time)
                return false;
            const eventStart = new Date(event.scheduled_start_time);
            const eventEnd = event.scheduled_end_time ? new Date(event.scheduled_end_time) : eventStart;
            // Include event if it overlaps with the selected day at all
            // Event starts before day ends AND event ends after day starts
            return eventStart <= dayEnd && eventEnd >= dayStart;
        });
    };
    const formatTime = (dateString) => {
        // Format time in user's selected timezone
        if (!dateString)
            return '';
        try {
            return formatTimeInTimezone(dateString, userTimezone);
        }
        catch (e) {
            console.error('Error formatting time:', e, dateString);
            return '';
        }
    };
    const formatDuration = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours === 0) {
            return `${mins} min`;
        }
        else if (mins === 0) {
            return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
        }
        else {
            return `${hours} ${hours === 1 ? 'hour' : 'hours'} and ${mins} min`;
        }
    };
    // Helper to check if event is a TaskList
    const isTaskList = (event) => {
        return 'name' in event && !('label' in event) && !('terrain_id' in event);
    };
    // Helper to check if event is a Length
    const isLength = (event) => {
        return 'name' in event && !('label' in event) && 'terrain_id' in event;
    };
    // Helper to get event title
    const getEventTitle = (event) => {
        if (isTaskList(event) || isLength(event)) {
            return event.name;
        }
        return event.label;
    };
    // Merge events, task_lists, and lengths for calendar display
    const getAllCalendarEvents = () => {
        // Convert TaskList to match CalendarEvent structure for display
        const taskListEvents = taskLists.map(tl => ({
            ...tl,
            label: tl.name,
            isbuildhabit: false,
            attendees: [],
            user_id: tl.user_id,
        }));
        // Convert Length to match CalendarEvent structure for display
        const lengthEvents = lengths.map(length => ({
            ...length,
            label: length.name,
            attendees: [],
            user_id: length.user_id,
        }));
        const combined = [...events, ...taskListEvents, ...lengthEvents];
        console.log('📊 getAllCalendarEvents - steps:', events.length, 'taskLists:', taskListEvents.length, 'lengths:', lengthEvents.length, 'total:', combined.length);
        // Debug: Check for duplicate IDs
        const ids = combined.map(e => e.id);
        const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
        if (duplicateIds.length > 0) {
            console.warn('⚠️ DUPLICATE IDs FOUND:', duplicateIds);
            console.warn('Events with duplicates:', combined.filter(e => duplicateIds.includes(e.id)));
        }
        // Sort by start time
        combined.sort((a, b) => {
            const aTime = a.scheduled_start_time ? new Date(a.scheduled_start_time).getTime() : 0;
            const bTime = b.scheduled_start_time ? new Date(b.scheduled_start_time).getTime() : 0;
            return aTime - bTime;
        });
        return combined;
    };
    const days = getDaysInMonth(currentDate);
    return (_jsxs("div", { className: "flex flex-col h-full", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsx("div", { className: "px-8 py-6 border-b", style: { borderColor: 'var(--border-color)' }, children: _jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsx("button", { onClick: () => navigate('/app/my-office'), className: "p-2 rounded hover:bg-opacity-80", style: { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }, children: _jsx(ArrowLeft, { className: "w-5 h-5" }) }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-10 h-10 rounded-lg flex items-center justify-center", style: { backgroundColor: '#EC489920' }, children: _jsx(CalendarIcon, { className: "w-6 h-6", style: { color: '#EC4899' } }) }), _jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-light", style: { color: 'var(--text-primary)' }, children: "Calendar" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Schedule & time management" })] })] })] }), _jsxs("div", { className: "relative", children: [_jsxs(Button, { onClick: () => setShowCreateDropdown(!showCreateDropdown), style: { backgroundColor: '#EC4899', color: 'white' }, children: [_jsx(Plus, { className: "w-4 h-4 mr-2" }), "New Event", _jsx(ChevronDown, { className: "w-4 h-4 ml-2" })] }), showCreateDropdown && (_jsxs("div", { className: "absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50", style: { backgroundColor: 'var(--bg-elevated)', borderColor: 'var(--border-color)' }, children: [_jsxs("button", { onClick: () => openCreateModal(undefined, 'step'), className: "w-full text-left px-4 py-3 hover:bg-opacity-80 transition-all flex items-center gap-3 rounded-t-lg", style: { backgroundColor: 'transparent', color: 'var(--text-primary)' }, children: [_jsx("span", { className: "text-lg", children: "\uD83D\uDCC5" }), _jsxs("div", { children: [_jsx("div", { className: "font-medium", children: "Step" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: "Habit or Activity" })] })] }), _jsxs("button", { onClick: () => openCreateModal(undefined, 'step_list'), className: "w-full text-left px-4 py-3 hover:bg-opacity-80 transition-all flex items-center gap-3", style: { backgroundColor: 'transparent', color: 'var(--text-primary)' }, children: [_jsx("span", { className: "text-lg", children: "\uD83D\uDCCB" }), _jsxs("div", { children: [_jsx("div", { className: "font-medium", children: "Step List" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: "Group of steps" })] })] }), _jsxs("button", { onClick: () => openCreateModal(undefined, 'length'), className: "w-full text-left px-4 py-3 hover:bg-opacity-80 transition-all flex items-center gap-3 rounded-b-lg", style: { backgroundColor: 'transparent', color: 'var(--text-primary)' }, children: [_jsx("span", { className: "text-lg", children: "\uD83D\uDCCF" }), _jsxs("div", { children: [_jsx("div", { className: "font-medium", children: "Length" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: "Time block with steps" })] })] })] }))] })] }) }), _jsxs("div", { className: "flex-1 overflow-hidden flex", children: [showLeftPanel && (_jsx("div", { className: "w-80 border-r overflow-auto transition-all", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: _jsxs("div", { className: "p-4", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Inbox, { className: "w-5 h-5", style: { color: '#EC4899' } }), _jsx("h3", { className: "text-lg font-medium", style: { color: 'var(--text-primary)' }, children: "Inbox" })] }), _jsx("span", { className: "text-xs px-2 py-1 rounded", style: { backgroundColor: '#EC489920', color: '#EC4899' }, children: unscheduledSteps.length })] }), _jsx("div", { className: "space-y-2", children: unscheduledSteps.length === 0 ? (_jsxs("div", { className: "text-center py-8", style: { color: 'var(--text-muted)' }, children: [_jsx(Inbox, { className: "w-12 h-12 mx-auto mb-2 opacity-50" }), _jsx("p", { className: "text-sm", children: "No unscheduled tasks" }), _jsxs(Button, { onClick: () => openCreateModal(), variant: "ghost", size: "sm", className: "mt-4", children: [_jsx(Plus, { className: "w-4 h-4 mr-2" }), "Add Task"] })] })) : (unscheduledSteps.map((step) => (_jsxs("div", { className: "group relative p-3 rounded-lg border hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-base)', borderColor: 'var(--border-color)' }, children: [_jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [step.isbuildhabit ? (_jsx(Repeat, { className: "w-4 h-4 flex-shrink-0", style: { color: step.color || '#3B82F6' }, title: "Habit" })) : (_jsx(CheckSquare, { className: "w-4 h-4 flex-shrink-0", style: { color: step.color || '#3B82F6' }, title: "Activity" })), _jsx("h4", { className: "font-medium truncate", style: { color: 'var(--text-primary)' }, children: step.label })] }), _jsxs("div", { className: "flex items-center gap-1 flex-shrink-0", children: [_jsx("div", { className: "w-3 h-3 rounded-full", style: { backgroundColor: step.color || '#3B82F6' } }), _jsxs("div", { className: "flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity", children: [_jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            openQuickEditModal(step);
                                                                        }, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-elevated)' }, title: "Quick Edit", children: _jsx(Edit2, { className: "w-3 h-3", style: { color: 'var(--text-secondary)' } }) }), _jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            quickScheduleStep(step);
                                                                        }, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-elevated)' }, title: "Schedule for Today", children: _jsx(CalendarIcon, { className: "w-3 h-3", style: { color: 'var(--text-secondary)' } }) }), _jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            deleteInboxStep(step.id);
                                                                        }, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-elevated)' }, title: "Delete", children: _jsx(Trash2, { className: "w-3 h-3", style: { color: '#EF4444' } }) })] })] })] }), step.description && (_jsx("p", { className: "text-xs mb-2 line-clamp-2", style: { color: 'var(--text-secondary)' }, children: step.description })), step.duration && step.duration > 0 && (_jsxs("div", { className: "flex items-center gap-2 text-xs", style: { color: 'var(--text-tertiary)' }, children: [_jsx(Clock, { className: "w-3 h-3" }), _jsxs("span", { children: [step.duration, " min"] })] }))] }, step.id)))) })] }) })), showListPanel && (_jsx("div", { className: "w-96 border-r overflow-auto transition-all", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: _jsxs("div", { className: "p-4", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(List, { className: "w-5 h-5", style: { color: '#3B82F6' } }), _jsx("h3", { className: "text-lg font-medium", style: { color: 'var(--text-primary)' }, children: "All Events" })] }), _jsx("span", { className: "text-xs px-2 py-1 rounded", style: { backgroundColor: '#3B82F620', color: '#3B82F6' }, children: events.length })] }), _jsx("div", { className: "space-y-2", children: events.length === 0 ? (_jsxs("div", { className: "text-center py-8", style: { color: 'var(--text-muted)' }, children: [_jsx(CalendarIcon, { className: "w-12 h-12 mx-auto mb-2 opacity-50" }), _jsx("p", { className: "text-sm", children: "No scheduled events" })] })) : (events
                                        .sort((a, b) => {
                                        if (!a.scheduled_start_time || !b.scheduled_start_time)
                                            return 0;
                                        return new Date(a.scheduled_start_time).getTime() - new Date(b.scheduled_start_time).getTime();
                                    })
                                        .map((event) => (_jsxs("div", { className: "group relative p-3 rounded-lg border hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-base)', borderColor: 'var(--border-color)' }, children: [_jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [event.isbuildhabit ? (_jsx(Repeat, { className: "w-4 h-4 flex-shrink-0", style: { color: event.color || '#3B82F6' }, title: "Habit" })) : (_jsx(CheckSquare, { className: "w-4 h-4 flex-shrink-0", style: { color: event.color || '#3B82F6' }, title: "Activity" })), _jsx("h4", { className: "font-medium truncate", style: { color: 'var(--text-primary)' }, children: event.label })] }), _jsxs("div", { className: "flex items-center gap-1 flex-shrink-0", children: [_jsx("div", { className: "w-3 h-3 rounded-full", style: { backgroundColor: event.color || '#3B82F6' } }), _jsxs("div", { className: "flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity", children: [_jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            toggleEventType(event);
                                                                        }, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-elevated)' }, title: event.isbuildhabit ? "Convert to Activity" : "Convert to Habit", children: event.isbuildhabit ? (_jsx(CheckSquare, { className: "w-3 h-3", style: { color: 'var(--text-secondary)' } })) : (_jsx(Repeat, { className: "w-3 h-3", style: { color: 'var(--text-secondary)' } })) }), _jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            openRescheduleModal(event);
                                                                        }, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-elevated)' }, title: "Reschedule", children: _jsx(CalendarClock, { className: "w-3 h-3", style: { color: 'var(--text-secondary)' } }) }), _jsx("button", { onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            unscheduleEvent(event);
                                                                        }, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-elevated)' }, title: "Unschedule (U)", children: _jsx(Inbox, { className: "w-3 h-3", style: { color: '#F59E0B' } }) })] })] })] }), event.scheduled_start_time && (_jsxs("div", { className: "flex items-center gap-2 text-xs mb-1", style: { color: 'var(--text-secondary)' }, children: [_jsx(Clock, { className: "w-3 h-3" }), _jsxs("span", { children: [new Date(event.scheduled_start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), " at ", new Date(event.scheduled_start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })] })] })), event.description && (_jsx("p", { className: "text-xs line-clamp-2", style: { color: 'var(--text-secondary)' }, children: event.description }))] }, event.id)))) })] }) })), _jsxs("div", { className: "flex-1 p-6 overflow-auto relative", children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsx("div", { className: "flex items-center gap-4", children: _jsxs("h2", { className: "text-2xl font-medium", style: { color: 'var(--text-primary)' }, children: [viewMode === 'month' && `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`, viewMode === 'week' && `Week of ${getWeekDays(currentDate)[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, viewMode === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })] }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => setShowTopWeekPanel(!showTopWeekPanel), className: "p-2 rounded hover:bg-opacity-80 transition-all", style: {
                                                    backgroundColor: showTopWeekPanel ? '#EC489920' : 'var(--bg-elevated)',
                                                    color: showTopWeekPanel ? '#EC4899' : 'var(--text-secondary)'
                                                }, title: "Toggle Week Overview", children: showTopWeekPanel ? _jsx(ChevronUp, { className: "w-5 h-5" }) : _jsx(ChevronDown, { className: "w-5 h-5" }) }), _jsx("button", { onClick: () => setShowLeftPanel(!showLeftPanel), className: "p-2 rounded hover:bg-opacity-80 transition-all", style: {
                                                    backgroundColor: showLeftPanel ? '#EC489920' : 'var(--bg-elevated)',
                                                    color: showLeftPanel ? '#EC4899' : 'var(--text-secondary)'
                                                }, title: "Toggle Inbox Panel", children: _jsx(Inbox, { className: "w-5 h-5" }) }), _jsx("button", { onClick: () => setShowListPanel(!showListPanel), className: "p-2 rounded hover:bg-opacity-80 transition-all", style: {
                                                    backgroundColor: showListPanel ? '#3B82F620' : 'var(--bg-elevated)',
                                                    color: showListPanel ? '#3B82F6' : 'var(--text-secondary)'
                                                }, title: "Toggle List View Panel", children: _jsx(List, { className: "w-5 h-5" }) }), _jsx("button", { onClick: () => setShowRightPanel(!showRightPanel), className: "p-2 rounded hover:bg-opacity-80 transition-all", style: {
                                                    backgroundColor: showRightPanel ? '#EC489920' : 'var(--bg-elevated)',
                                                    color: showRightPanel ? '#EC4899' : 'var(--text-secondary)'
                                                }, title: "Toggle Events Panel", children: _jsx(PanelRight, { className: "w-5 h-5" }) }), _jsxs("div", { className: "flex items-center gap-1 p-1 rounded", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsx("button", { onClick: () => setViewMode('month'), className: `px-3 py-1 rounded text-sm transition-all ${viewMode === 'month' ? 'font-medium' : ''}`, style: {
                                                            backgroundColor: viewMode === 'month' ? '#EC4899' : 'transparent',
                                                            color: viewMode === 'month' ? 'white' : 'var(--text-secondary)'
                                                        }, children: "Month" }), _jsx("button", { onClick: () => setViewMode('week'), className: `px-3 py-1 rounded text-sm transition-all ${viewMode === 'week' ? 'font-medium' : ''}`, style: {
                                                            backgroundColor: viewMode === 'week' ? '#EC4899' : 'transparent',
                                                            color: viewMode === 'week' ? 'white' : 'var(--text-secondary)'
                                                        }, children: "Week" }), _jsx("button", { onClick: () => setViewMode('day'), className: `px-3 py-1 rounded text-sm transition-all ${viewMode === 'day' ? 'font-medium' : ''}`, style: {
                                                            backgroundColor: viewMode === 'day' ? '#EC4899' : 'transparent',
                                                            color: viewMode === 'day' ? 'white' : 'var(--text-secondary)'
                                                        }, children: "Day" })] }), _jsx("button", { onClick: handlePrev, className: "p-2 rounded hover:bg-opacity-80", style: { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }, children: _jsx(ChevronLeft, { className: "w-5 h-5" }) }), _jsx("button", { onClick: () => setCurrentDate(new Date()), className: "px-4 py-2 rounded text-sm", style: { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }, children: "Today" }), _jsx("button", { onClick: handleNext, className: "p-2 rounded hover:bg-opacity-80", style: { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }, children: _jsx(ChevronRight, { className: "w-5 h-5" }) })] })] }), showTopWeekPanel && (_jsxs("div", { className: "absolute top-0 left-0 right-0 z-10 shadow-2xl transition-all duration-300 ease-in-out", style: {
                                    backgroundColor: 'var(--bg-elevated)',
                                    borderBottom: '2px solid var(--border-color)',
                                    animation: 'slideDown 0.3s ease-out'
                                }, children: [_jsx("style", { children: `
                  @keyframes slideDown {
                    from {
                      transform: translateY(-100%);
                      opacity: 0;
                    }
                    to {
                      transform: translateY(0);
                      opacity: 1;
                    }
                  }
                ` }), _jsxs("div", { className: "p-4", children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsxs("h3", { className: "text-lg font-medium", style: { color: 'var(--text-primary)' }, children: ["Week Overview - ", getWeekDays(currentDate)[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), " to ", getWeekDays(currentDate)[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })] }), _jsx("button", { onClick: () => setShowTopWeekPanel(false), className: "p-1 rounded hover:bg-opacity-80", style: { backgroundColor: 'var(--bg-base)', color: 'var(--text-secondary)' }, children: _jsx(X, { className: "w-4 h-4" }) })] }), _jsxs("div", { className: "rounded-lg overflow-hidden border", style: { borderColor: 'var(--border-color)' }, children: [_jsxs("div", { className: "grid grid-cols-8", children: [_jsx("div", { className: "border-b p-2 text-xs font-medium", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-base)', color: 'var(--text-secondary)' }, children: "Time" }), getWeekDays(currentDate).map((date, idx) => {
                                                                const isToday = new Date().toDateString() === date.toDateString();
                                                                const isCurrentDay = currentDate.toDateString() === date.toDateString();
                                                                return (_jsxs("div", { className: "border-b border-l p-2 text-center cursor-pointer hover:bg-opacity-80", style: {
                                                                        borderColor: 'var(--border-color)',
                                                                        backgroundColor: isCurrentDay ? '#EC489925' : isToday ? '#EC489915' : 'var(--bg-base)'
                                                                    }, onClick: () => {
                                                                        setCurrentDate(date);
                                                                        setSelectedDate(date);
                                                                    }, children: [_jsx("div", { className: "text-xs font-medium", style: { color: 'var(--text-secondary)' }, children: DAYS[date.getDay()] }), _jsx("div", { className: `text-base ${isToday || isCurrentDay ? 'font-bold' : ''}`, style: { color: isCurrentDay ? '#EC4899' : isToday ? '#EC4899' : 'var(--text-primary)' }, children: date.getDate() })] }, idx));
                                                            })] }), _jsx("div", { className: "overflow-y-auto max-h-[400px]", children: getTimeSlots().map((timeSlot, hour) => (_jsxs("div", { className: "grid grid-cols-8", children: [_jsx("div", { className: "border-b p-2 text-xs", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-base)', color: 'var(--text-tertiary)' }, children: timeSlot }), getWeekDays(currentDate).map((date, idx) => {
                                                                    const dayEvents = getEventsForDateTime(date, hour);
                                                                    return (_jsx("button", { onClick: () => {
                                                                            const clickedDate = new Date(date);
                                                                            clickedDate.setHours(hour);
                                                                            openCreateModal(clickedDate);
                                                                        }, className: "border-b border-l p-1 min-h-[50px] hover:bg-opacity-50 transition-colors relative", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: dayEvents.map((event, eventIdx) => {
                                                                            const spilloverDays = getEventSpilloverDays(event);
                                                                            const hasSpillover = spilloverDays > 0;
                                                                            return (_jsxs("div", { onClick: (e) => {
                                                                                    e.stopPropagation();
                                                                                    openEditModal(event);
                                                                                }, className: "text-xs p-1 mb-1 rounded cursor-pointer hover:opacity-80 flex items-center gap-1", style: { backgroundColor: event.color, color: 'white' }, children: [event.isbuildhabit ? _jsx(Repeat, { className: "w-3 h-3" }) : _jsx(CheckSquare, { className: "w-3 h-3" }), _jsx("span", { className: "truncate flex-1", children: event.label }), hasSpillover && (_jsxs("span", { className: "text-[10px] opacity-75 whitespace-nowrap", children: ["\u2192+", spilloverDays, "d"] }))] }, eventIdx));
                                                                        }) }, idx));
                                                                })] }, timeSlot))) })] })] })] })), viewMode === 'month' && (_jsxs("div", { className: "rounded-lg overflow-hidden border", style: { borderColor: 'var(--border-color)' }, children: [_jsx("div", { className: "grid grid-cols-7 border-b", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: DAYS.map((day) => (_jsx("div", { className: "p-3 text-center text-sm font-medium", style: { color: 'var(--text-secondary)' }, children: day }, day))) }), _jsx("div", { className: "grid grid-cols-7", children: days.map((day, index) => (_jsx("button", { onClick: () => {
                                                if (day) {
                                                    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
                                                }
                                            }, disabled: !day, className: `
                      aspect-square p-2 border-b border-r transition-colors
                      ${day ? 'hover:bg-opacity-50 cursor-pointer' : 'cursor-default'}
                      ${isSelectedDate(day || 0) ? 'bg-opacity-20' : ''}
                    `, style: {
                                                borderColor: 'var(--border-color)',
                                                backgroundColor: isSelectedDate(day || 0) ? '#EC4899' : isToday(day || 0) ? '#EC489910' : 'var(--bg-base)',
                                                color: day ? 'var(--text-primary)' : 'var(--text-muted)'
                                            }, children: day && (_jsxs("div", { className: "flex flex-col h-full", children: [_jsxs("div", { className: "flex items-center justify-center mb-1", children: [_jsx("span", { className: `text-sm ${isToday(day) ? 'font-bold' : ''}`, children: day }), isToday(day) && (_jsx("div", { className: "w-1 h-1 rounded-full ml-1", style: { backgroundColor: '#EC4899' } }))] }), _jsxs("div", { className: "flex flex-wrap gap-0.5 justify-center mt-auto", children: [getEventsForDay(day).slice(0, 3).map((event, idx) => {
                                                                const spilloverDays = getEventSpilloverDays(event);
                                                                const hasSpillover = spilloverDays > 0;
                                                                return (_jsx("div", { className: "relative w-1.5 h-1.5 rounded-full flex items-center", style: { backgroundColor: event.color }, title: hasSpillover ? `${event.label} (spans ${spilloverDays + 1} day${spilloverDays > 0 ? 's' : ''})` : event.label, children: hasSpillover && (_jsx("span", { className: "absolute -right-1 text-[6px]", style: { color: event.color }, children: "\u2192" })) }, idx));
                                                            }), getEventsForDay(day).length > 3 && (_jsxs("span", { className: "text-[8px]", style: { color: 'var(--text-tertiary)' }, children: ["+", getEventsForDay(day).length - 3] }))] })] })) }, index))) })] })), viewMode === 'week' && (_jsxs("div", { className: "rounded-lg overflow-hidden border", style: { borderColor: 'var(--border-color)' }, children: [_jsxs("div", { className: "grid grid-cols-8", children: [_jsx("div", { className: "border-b p-2 text-xs font-medium", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }, children: "Time" }), getWeekDays(currentDate).map((date, idx) => {
                                                const isToday = new Date().toDateString() === date.toDateString();
                                                return (_jsxs("div", { className: "border-b border-l p-2 text-center", style: { borderColor: 'var(--border-color)', backgroundColor: isToday ? '#EC489915' : 'var(--bg-elevated)' }, children: [_jsx("div", { className: "text-xs font-medium", style: { color: 'var(--text-secondary)' }, children: DAYS[date.getDay()] }), _jsx("div", { className: `text-lg ${isToday ? 'font-bold' : ''}`, style: { color: isToday ? '#EC4899' : 'var(--text-primary)' }, children: date.getDate() })] }, idx));
                                            })] }), _jsx("div", { className: "overflow-y-auto max-h-[600px]", children: getTimeSlots().map((timeSlot, hour) => (_jsxs("div", { className: "grid grid-cols-8", children: [_jsx("div", { className: "border-b p-2 text-xs", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)', color: 'var(--text-tertiary)' }, children: timeSlot }), getWeekDays(currentDate).map((date, idx) => {
                                                    const dayEvents = getEventsForDateTime(date, hour);
                                                    return (_jsx("button", { onClick: () => {
                                                            const clickedDate = new Date(date);
                                                            clickedDate.setHours(hour);
                                                            openCreateModal(clickedDate);
                                                        }, className: "border-b border-l p-1 min-h-[60px] hover:bg-opacity-50 transition-colors relative", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-base)' }, children: dayEvents.map((event, eventIdx) => {
                                                            const spilloverDays = getEventSpilloverDays(event);
                                                            const hasSpillover = spilloverDays > 0;
                                                            return (_jsxs("div", { onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    openEditModal(event);
                                                                }, className: "text-xs p-1 mb-1 rounded cursor-pointer hover:opacity-80 flex items-center gap-1", style: { backgroundColor: event.color, color: 'white' }, children: [event.isbuildhabit ? _jsx(Repeat, { className: "w-3 h-3" }) : _jsx(CheckSquare, { className: "w-3 h-3" }), _jsx("span", { className: "truncate flex-1", children: event.label }), hasSpillover && (_jsxs("span", { className: "text-[10px] opacity-75 whitespace-nowrap", children: ["\u2192+", spilloverDays, "d"] }))] }, eventIdx));
                                                        }) }, idx));
                                                })] }, timeSlot))) })] })), viewMode === 'day' && (_jsx("div", { className: "max-w-3xl mx-auto py-6", children: _jsxs("div", { className: "space-y-0 relative", children: [(() => {
                                            const dayEvents = getEventsForSelectedDate().sort((a, b) => {
                                                if (!a.scheduled_start_time || !b.scheduled_start_time)
                                                    return 0;
                                                return new Date(a.scheduled_start_time).getTime() - new Date(b.scheduled_start_time).getTime();
                                            });
                                            const now = new Date();
                                            const gapMessages = [
                                                "A well-spent interval.",
                                                "Past moments, gained insights.",
                                                "A window of potential.",
                                                "Time flows forward.",
                                                "Breathing room."
                                            ];
                                            return dayEvents.map((event, index, array) => {
                                                if (!event.scheduled_start_time || !event.scheduled_end_time)
                                                    return null;
                                                const startTime = new Date(event.scheduled_start_time);
                                                const endTime = new Date(event.scheduled_end_time);
                                                const durationMs = endTime.getTime() - startTime.getTime();
                                                const durationMinutes = Math.round(durationMs / (1000 * 60));
                                                // Calculate elapsed percentage
                                                const elapsed = now.getTime() - startTime.getTime();
                                                const elapsedPercent = Math.min(Math.max((elapsed / durationMs) * 100, 0), 100);
                                                const isPast = now >= endTime;
                                                const isCurrent = now >= startTime && now < endTime;
                                                const isFuture = now < startTime;
                                                // Calculate remaining time for current events
                                                let remainingMinutes = 0;
                                                if (isCurrent) {
                                                    remainingMinutes = Math.round((endTime.getTime() - now.getTime()) / (1000 * 60));
                                                }
                                                // Calculate height (minimum 80px for readability, scale up to 3px per minute)
                                                const pillHeight = Math.max(80, Math.min(durationMinutes * 3, 400));
                                                // Check for gap to next event
                                                const nextEvent = array[index + 1];
                                                let gapMinutes = 0;
                                                if (nextEvent && nextEvent.scheduled_start_time) {
                                                    const nextStartTime = new Date(nextEvent.scheduled_start_time);
                                                    gapMinutes = Math.round((nextStartTime.getTime() - endTime.getTime()) / (1000 * 60));
                                                }
                                                return (_jsxs(React.Fragment, { children: [_jsxs("div", { className: "flex items-center gap-3 mb-0 relative", style: { zIndex: 1 }, children: [_jsx("div", { className: "w-20 flex-shrink-0 text-right", children: _jsx("div", { className: "text-sm font-medium", style: { color: 'var(--text-tertiary)' }, children: formatTime(event.scheduled_start_time) }) }), _jsx("div", { className: "w-24 flex-shrink-0", children: _jsxs("div", { className: "relative rounded-full overflow-hidden", style: {
                                                                            height: `${pillHeight}px`,
                                                                            border: isFuture ? `2px solid ${event.color}` : 'none',
                                                                            backgroundColor: isFuture ? 'transparent' : 'transparent'
                                                                        }, children: [isPast && (_jsx("div", { className: "absolute left-0 top-0 right-0 bottom-0 rounded-full", style: {
                                                                                    backgroundColor: event.color,
                                                                                } })), isCurrent && (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute left-0 top-0 right-0 rounded-full transition-all duration-1000", style: {
                                                                                            height: `${elapsedPercent}%`,
                                                                                            backgroundColor: event.color,
                                                                                        } }), _jsx("div", { className: "absolute left-0 right-0 bottom-0 rounded-full", style: {
                                                                                            top: `${elapsedPercent}%`,
                                                                                            border: `2px solid ${event.color}`,
                                                                                            backgroundColor: 'transparent'
                                                                                        } })] })), _jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: _jsx("div", { className: "w-12 h-12 rounded-full flex items-center justify-center", style: {
                                                                                        backgroundColor: isPast || isCurrent ? 'rgba(255, 255, 255, 0.3)' : 'transparent',
                                                                                    }, children: event.isbuildhabit ? (_jsx(Repeat, { className: "w-6 h-6", style: { color: isPast || isCurrent ? 'white' : event.color } })) : event.icon ? (_jsx("span", { className: "text-xl", children: event.icon })) : (_jsx(CheckSquare, { className: "w-6 h-6", style: { color: isPast || isCurrent ? 'white' : event.color } })) }) })] }) }), _jsxs("div", { className: "flex-1 group", children: [_jsxs("div", { className: "cursor-pointer hover:opacity-80 transition-opacity", onClick: () => openEditModal(event), children: [_jsxs("div", { className: "flex items-center gap-2 mb-1", children: [_jsxs("span", { className: "text-xs font-medium", style: { color: 'var(--text-tertiary)' }, children: [formatTime(event.scheduled_start_time), " - ", formatTime(event.scheduled_end_time), " (", formatDuration(durationMinutes), ")"] }), event.isbuildhabit && (_jsx("span", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: "\u21BB" })), isCurrent && remainingMinutes > 0 && (_jsxs("span", { className: "text-xs font-medium", style: { color: event.color }, children: [remainingMinutes, "m remaining"] }))] }), _jsx("div", { className: "font-bold text-base mb-1", style: { color: 'var(--text-primary)' }, children: event.label }), event.id && breathCounts.has(event.id) && breathCounts.get(event.id).total > 0 && (_jsxs("div", { className: "text-xs flex items-center gap-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(CheckSquare, { className: "w-3 h-3" }), _jsxs("span", { children: [breathCounts.get(event.id).completed, "/", breathCounts.get(event.id).total] })] }))] }), _jsxs("div", { className: "opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 mt-2", children: [_jsx("button", { onClick: async (e) => {
                                                                                        e.stopPropagation();
                                                                                        if (!event.id)
                                                                                            return;
                                                                                        // Determine if this is a habit instance
                                                                                        const isHabitInstance = event.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && event.isbuildhabit;
                                                                                        const actualId = extractActualId(event.id, isHabitInstance);
                                                                                        // -1 = Move to late previous day (appears at start of current day's flow)
                                                                                        const currentStart = new Date(event.scheduled_start_time);
                                                                                        const currentEnd = new Date(event.scheduled_end_time);
                                                                                        const duration = currentEnd.getTime() - currentStart.getTime();
                                                                                        const newStart = new Date(selectedDate);
                                                                                        newStart.setDate(newStart.getDate() - 1);
                                                                                        newStart.setHours(23, 0, 0, 0);
                                                                                        const newEnd = new Date(newStart.getTime() + duration);
                                                                                        const { error } = await supabase
                                                                                            .from('steps')
                                                                                            .update({
                                                                                            scheduled_start_time: newStart.toISOString(),
                                                                                            scheduled_end_time: newEnd.toISOString()
                                                                                        })
                                                                                            .eq('id', actualId);
                                                                                        if (!error)
                                                                                            await fetchEvents();
                                                                                    }, className: "px-2 py-0.5 rounded text-xs", style: {
                                                                                        backgroundColor: 'var(--bg-base)',
                                                                                        border: '1px solid var(--border-color)',
                                                                                        color: 'var(--text-secondary)'
                                                                                    }, title: "Move to late previous day (start of today's flow)", children: "-1" }), _jsx("button", { onClick: async (e) => {
                                                                                        e.stopPropagation();
                                                                                        if (!event.id)
                                                                                            return;
                                                                                        // Determine if this is a habit instance
                                                                                        const isHabitInstance = event.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && event.isbuildhabit;
                                                                                        const actualId = extractActualId(event.id, isHabitInstance);
                                                                                        // ±0 = Move to normal day hours (middle of current day's flow)
                                                                                        const currentStart = new Date(event.scheduled_start_time);
                                                                                        const currentEnd = new Date(event.scheduled_end_time);
                                                                                        const duration = currentEnd.getTime() - currentStart.getTime();
                                                                                        const newStart = new Date(selectedDate);
                                                                                        newStart.setHours(12, 0, 0, 0);
                                                                                        const newEnd = new Date(newStart.getTime() + duration);
                                                                                        const { error } = await supabase
                                                                                            .from('steps')
                                                                                            .update({
                                                                                            scheduled_start_time: newStart.toISOString(),
                                                                                            scheduled_end_time: newEnd.toISOString()
                                                                                        })
                                                                                            .eq('id', actualId);
                                                                                        if (!error)
                                                                                            await fetchEvents();
                                                                                    }, className: "px-2 py-0.5 rounded text-xs", style: {
                                                                                        backgroundColor: 'var(--bg-base)',
                                                                                        border: '1px solid var(--border-color)',
                                                                                        color: 'var(--text-secondary)'
                                                                                    }, title: "Move to normal hours (middle of today's flow)", children: "\u00B10" }), _jsx("button", { onClick: async (e) => {
                                                                                        e.stopPropagation();
                                                                                        if (!event.id)
                                                                                            return;
                                                                                        // Determine if this is a habit instance
                                                                                        const isHabitInstance = event.id.match(/^(.+)-(\d{4})-(\d{2})-(\d{2})$/) !== null && event.isbuildhabit;
                                                                                        const actualId = extractActualId(event.id, isHabitInstance);
                                                                                        // +1 = Move to early next day (appears at end of current day's flow)
                                                                                        const currentStart = new Date(event.scheduled_start_time);
                                                                                        const currentEnd = new Date(event.scheduled_end_time);
                                                                                        const duration = currentEnd.getTime() - currentStart.getTime();
                                                                                        const newStart = new Date(selectedDate);
                                                                                        newStart.setDate(newStart.getDate() + 1);
                                                                                        newStart.setHours(1, 0, 0, 0);
                                                                                        const newEnd = new Date(newStart.getTime() + duration);
                                                                                        const { error } = await supabase
                                                                                            .from('steps')
                                                                                            .update({
                                                                                            scheduled_start_time: newStart.toISOString(),
                                                                                            scheduled_end_time: newEnd.toISOString()
                                                                                        })
                                                                                            .eq('id', actualId);
                                                                                        if (!error)
                                                                                            await fetchEvents();
                                                                                    }, className: "px-2 py-0.5 rounded text-xs", style: {
                                                                                        backgroundColor: 'var(--bg-base)',
                                                                                        border: '1px solid var(--border-color)',
                                                                                        color: 'var(--text-secondary)'
                                                                                    }, title: "Move to early next day (end of today's flow)", children: "+1" })] })] }), _jsx("div", { className: "flex-shrink-0", children: _jsx("div", { className: `w-8 h-8 rounded-full border-2 relative overflow-hidden ${event.isbuildhabit ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`, style: {
                                                                            borderColor: event.status === 'cancelled' ? event.color : event.completed ? event.color : `${event.color}50`,
                                                                            backgroundColor: event.status === 'cancelled' ? 'transparent' : event.completed ? event.color : 'transparent'
                                                                        }, onClick: (e) => {
                                                                            e.stopPropagation();
                                                                            event.isbuildhabit && toggleHabitCompletion(event, e);
                                                                        }, children: event.status === 'cancelled' && (_jsx("div", { className: "absolute inset-0", style: {
                                                                                backgroundImage: `linear-gradient(to top right, ${event.color} 0%, ${event.color} 50%, transparent 50%, transparent 100%)`
                                                                            } })) }) })] }), index < array.length - 1 && nextEvent && (_jsxs("div", { className: "flex gap-3 relative", children: [_jsx("div", { className: "w-20 flex-shrink-0" }), _jsxs("div", { className: "flex flex-col items-center", style: { width: '96px' }, children: [_jsx("div", { style: {
                                                                                width: '2px',
                                                                                height: gapMinutes > 15 ? '16px' : `${Math.max(12, gapMinutes * 2)}px`,
                                                                                background: 'repeating-linear-gradient(to bottom, #888888 0, #888888 4px, transparent 4px, transparent 8px)',
                                                                                marginTop: '-2px'
                                                                            } }), gapMinutes > 15 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "text-xs italic flex flex-col items-center gap-1 py-2", style: { color: 'var(--text-tertiary)' }, children: [_jsx("span", { children: "\uD83D\uDCAD" }), _jsx("span", { className: "text-center", children: gapMessages[index % gapMessages.length] }), _jsxs("span", { className: "font-medium", children: [Math.floor(gapMinutes / 60) > 0 && `${Math.floor(gapMinutes / 60)}h `, gapMinutes % 60, "m"] })] }), _jsx("div", { style: {
                                                                                        width: '2px',
                                                                                        height: '16px',
                                                                                        background: 'repeating-linear-gradient(to bottom, #888888 0, #888888 4px, transparent 4px, transparent 8px)',
                                                                                        marginBottom: '-2px'
                                                                                    } })] })), gapMinutes <= 15 && (_jsx("div", { style: { marginBottom: '-2px' } }))] }), gapMinutes > 15 && new Date(event.scheduled_end_time) >= now && (_jsx("div", { className: "flex-1 flex items-center", children: _jsxs("button", { onClick: () => {
                                                                            const gapStartTime = new Date(event.scheduled_end_time);
                                                                            openCreateModal(gapStartTime);
                                                                        }, className: "px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-all hover:opacity-80", style: {
                                                                            backgroundColor: 'var(--bg-elevated)',
                                                                            border: '1px solid var(--border-color)',
                                                                            color: 'var(--text-secondary)'
                                                                        }, children: [_jsx(Plus, { className: "w-4 h-4" }), "Add Task"] }) }))] }))] }, event.id));
                                            });
                                        })(), getEventsForSelectedDate().length === 0 && (_jsxs("div", { className: "text-center py-16", children: [_jsx(CalendarIcon, { className: "w-16 h-16 mx-auto mb-4 opacity-30", style: { color: 'var(--text-tertiary)' } }), _jsx("p", { className: "text-sm mb-4", style: { color: 'var(--text-muted)' }, children: "No events scheduled for this day" }), _jsxs(Button, { onClick: () => openCreateModal(), variant: "primary", className: "mx-auto", children: [_jsx(Plus, { className: "w-4 h-4 mr-2" }), "Add Task"] })] }))] }) }))] }), showRightPanel && (_jsx("div", { className: "w-80 border-l overflow-auto transition-all", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: _jsxs("div", { className: "p-4", children: [_jsx("h3", { className: "text-lg font-medium mb-4", style: { color: 'var(--text-primary)' }, children: selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) }), _jsxs("div", { className: "space-y-3", children: [getEventsForSelectedDate().map((event) => (_jsxs("button", { onClick: () => openEditModal(event), className: "w-full p-3 rounded-lg border text-left hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-base)', borderColor: 'var(--border-color)' }, children: [_jsxs("div", { className: "flex items-start justify-between mb-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [event.isbuildhabit ? (_jsx(Repeat, { className: "w-4 h-4 flex-shrink-0", style: { color: event.color }, title: "Habit" })) : (_jsx(CheckSquare, { className: "w-4 h-4 flex-shrink-0", style: { color: event.color }, title: "Activity" })), _jsx("h4", { className: "font-medium", style: { color: 'var(--text-primary)' }, children: event.label })] }), _jsx("div", { className: `w-3 h-3 rounded-full border-2 flex-shrink-0 ml-2 relative overflow-hidden ${event.isbuildhabit ? 'cursor-pointer hover:scale-125 transition-transform' : ''}`, style: {
                                                                borderColor: event.status === 'cancelled' ? event.color : event.completed ? event.color : 'var(--border-color)',
                                                                backgroundColor: event.status === 'cancelled' ? 'transparent' : event.completed ? event.color : 'transparent'
                                                            }, onClick: (e) => {
                                                                if (event.isbuildhabit) {
                                                                    e.stopPropagation();
                                                                    toggleHabitCompletion(event, e);
                                                                }
                                                            }, children: event.status === 'cancelled' && (_jsx("div", { className: "absolute inset-0", style: {
                                                                    backgroundImage: `linear-gradient(to top right, ${event.color} 0%, ${event.color} 50%, transparent 50%, transparent 100%)`
                                                                } })) })] }), event.description && (_jsx("p", { className: "text-xs mb-2 line-clamp-2", style: { color: 'var(--text-secondary)' }, children: event.description })), _jsxs("div", { className: "space-y-1 text-xs", style: { color: 'var(--text-secondary)' }, children: [event.scheduled_start_time && event.scheduled_end_time && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Clock, { className: "w-3 h-3" }), _jsxs("span", { children: [formatTime(event.scheduled_start_time), " - ", formatTime(event.scheduled_end_time)] })] })), event.location && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(MapPin, { className: "w-3 h-3" }), _jsx("span", { children: event.location })] })), event.attendees && event.attendees.length > 0 && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Users, { className: "w-3 h-3" }), _jsxs("span", { children: [event.attendees.length, " attendee", event.attendees.length > 1 ? 's' : ''] })] })), event.email_thread_id && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Mail, { className: "w-3 h-3" }), _jsx("span", { children: "Linked to email" })] }))] })] }, event.id))), getEventsForSelectedDate().length === 0 && (_jsxs("div", { className: "text-center py-8", style: { color: 'var(--text-muted)' }, children: [_jsx(CalendarIcon, { className: "w-12 h-12 mx-auto mb-2 opacity-50" }), _jsx("p", { className: "text-sm", children: "No events scheduled" }), _jsxs("div", { className: "flex gap-2 mt-4 justify-center", children: [_jsxs(Button, { onClick: () => {
                                                                const start = new Date(selectedDate);
                                                                start.setHours(9, 0, 0, 0);
                                                                const end = new Date(start);
                                                                end.setHours(10, 0, 0, 0);
                                                                setEventForm({
                                                                    label: '',
                                                                    description: '',
                                                                    scheduled_start_time: start.toISOString(),
                                                                    scheduled_end_time: end.toISOString(),
                                                                    isbuildhabit: true,
                                                                    color: '#EC4899'
                                                                });
                                                                setSelectedEvent(null);
                                                                setShowEventModal(true);
                                                            }, variant: "ghost", size: "sm", style: { backgroundColor: '#EC489915', color: '#EC4899' }, children: [_jsx(Repeat, { className: "w-4 h-4 mr-2" }), "Habit"] }), _jsxs(Button, { onClick: () => {
                                                                const start = new Date(selectedDate);
                                                                start.setHours(9, 0, 0, 0);
                                                                const end = new Date(start);
                                                                end.setHours(10, 0, 0, 0);
                                                                setEventForm({
                                                                    label: '',
                                                                    description: '',
                                                                    scheduled_start_time: start.toISOString(),
                                                                    scheduled_end_time: end.toISOString(),
                                                                    isbuildhabit: false,
                                                                    color: '#3B82F6'
                                                                });
                                                                setSelectedEvent(null);
                                                                setShowEventModal(true);
                                                            }, variant: "ghost", size: "sm", style: { backgroundColor: '#3B82F615', color: '#3B82F6' }, children: [_jsx(CheckSquare, { className: "w-4 h-4 mr-2" }), "Activity"] })] })] }))] })] }) }))] }), showRescheduleModal && rescheduleEvent && (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "w-full max-w-md rounded-lg", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "flex items-center justify-between p-4 border-b", style: { borderColor: 'var(--border-color)' }, children: [_jsx("h2", { className: "text-lg font-semibold", style: { color: 'var(--text-primary)' }, children: "Reschedule Event" }), _jsx("button", { onClick: closeRescheduleModal, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx(X, { className: "w-5 h-5", style: { color: 'var(--text-secondary)' } }) })] }), _jsxs("div", { className: "p-4 space-y-4", children: [_jsxs("div", { className: "flex items-center gap-2 p-3 rounded-lg", style: { backgroundColor: 'var(--bg-base)' }, children: [rescheduleEvent.isbuildhabit ? (_jsx(Repeat, { className: "w-4 h-4", style: { color: rescheduleEvent.color } })) : (_jsx(CheckSquare, { className: "w-4 h-4", style: { color: rescheduleEvent.color } })), _jsx("span", { className: "font-medium", style: { color: 'var(--text-primary)' }, children: rescheduleEvent.label })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "New Date & Time" }), _jsx("input", { type: "datetime-local", value: rescheduleEvent.scheduled_start_time ? new Date(rescheduleEvent.scheduled_start_time).toISOString().slice(0, 16) : '', onChange: (e) => {
                                                if (e.target.value) {
                                                    setRescheduleEvent({ ...rescheduleEvent, scheduled_start_time: new Date(e.target.value).toISOString() });
                                                }
                                            }, className: "w-full px-3 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                borderColor: 'var(--border-color)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Duration (minutes)" }), _jsx("input", { type: "number", value: rescheduleEvent.duration || 60, onChange: (e) => setRescheduleEvent({ ...rescheduleEvent, duration: parseInt(e.target.value) || 60 }), min: "5", step: "5", className: "w-full px-3 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                borderColor: 'var(--border-color)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            } })] })] }), _jsxs("div", { className: "flex gap-3 p-4 border-t", style: { borderColor: 'var(--border-color)' }, children: [_jsx("button", { onClick: closeRescheduleModal, className: "flex-1 px-4 py-2 rounded-lg font-medium transition-all", style: {
                                        backgroundColor: 'var(--bg-base)',
                                        color: 'var(--text-secondary)',
                                        border: '1px solid var(--border-color)'
                                    }, children: "Cancel" }), _jsx("button", { onClick: saveReschedule, disabled: loading || !rescheduleEvent.scheduled_start_time, className: "flex-1 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: '#3B82F6', color: 'white' }, children: loading ? 'Saving...' : 'Reschedule' })] })] }) })), showQuickEditModal && quickEditStep && (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "w-full max-w-md rounded-lg", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "flex items-center justify-between p-4 border-b", style: { borderColor: 'var(--border-color)' }, children: [_jsx("h2", { className: "text-lg font-semibold", style: { color: 'var(--text-primary)' }, children: "Quick Edit" }), _jsx("button", { onClick: closeQuickEditModal, className: "p-1 rounded hover:bg-opacity-80 transition-all", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx(X, { className: "w-5 h-5", style: { color: 'var(--text-secondary)' } }) })] }), _jsxs("div", { className: "p-4 space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Title" }), _jsx("input", { type: "text", value: quickEditStep.label, onChange: (e) => setQuickEditStep({ ...quickEditStep, label: e.target.value }), className: "w-full px-3 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                borderColor: 'var(--border-color)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            }, placeholder: "Task title" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Description" }), _jsx("textarea", { value: quickEditStep.description || '', onChange: (e) => setQuickEditStep({ ...quickEditStep, description: e.target.value }), rows: 2, className: "w-full px-3 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                borderColor: 'var(--border-color)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            }, placeholder: "Add description" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Type" }), _jsxs("div", { className: "flex gap-2", children: [_jsxs("button", { onClick: () => setQuickEditStep({ ...quickEditStep, isbuildhabit: false }), className: `flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-all ${!quickEditStep.isbuildhabit ? 'ring-2' : ''}`, style: {
                                                        backgroundColor: !quickEditStep.isbuildhabit ? '#3B82F620' : 'var(--bg-base)',
                                                        ringColor: '#3B82F6',
                                                        color: !quickEditStep.isbuildhabit ? '#3B82F6' : 'var(--text-secondary)',
                                                        border: '1px solid var(--border-color)'
                                                    }, children: [_jsx(CheckSquare, { className: "w-4 h-4" }), _jsx("span", { className: "text-sm font-medium", children: "Activity" })] }), _jsxs("button", { onClick: () => setQuickEditStep({ ...quickEditStep, isbuildhabit: true }), className: `flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-all ${quickEditStep.isbuildhabit ? 'ring-2' : ''}`, style: {
                                                        backgroundColor: quickEditStep.isbuildhabit ? '#EC489920' : 'var(--bg-base)',
                                                        ringColor: '#EC4899',
                                                        color: quickEditStep.isbuildhabit ? '#EC4899' : 'var(--text-secondary)',
                                                        border: '1px solid var(--border-color)'
                                                    }, children: [_jsx(Repeat, { className: "w-4 h-4" }), _jsx("span", { className: "text-sm font-medium", children: "Habit" })] })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Duration (minutes)" }), _jsx("input", { type: "number", value: quickEditStep.duration || 60, onChange: (e) => setQuickEditStep({ ...quickEditStep, duration: parseInt(e.target.value) || 60 }), min: "5", step: "5", className: "w-full px-3 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                borderColor: 'var(--border-color)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Schedule (Optional)" }), _jsx("input", { type: "datetime-local", value: quickEditStep.scheduled_start_time ? new Date(quickEditStep.scheduled_start_time).toISOString().slice(0, 16) : '', onChange: (e) => {
                                                if (e.target.value) {
                                                    setQuickEditStep({ ...quickEditStep, scheduled_start_time: new Date(e.target.value).toISOString() });
                                                }
                                                else {
                                                    setQuickEditStep({ ...quickEditStep, scheduled_start_time: null });
                                                }
                                            }, className: "w-full px-3 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                borderColor: 'var(--border-color)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Color" }), _jsx("div", { className: "flex gap-2", children: EVENT_COLORS.map((colorOption) => (_jsx("button", { onClick: () => setQuickEditStep({ ...quickEditStep, color: colorOption.value }), className: `w-8 h-8 rounded-full transition-all ${quickEditStep.color === colorOption.value ? 'ring-2 ring-offset-2' : ''}`, style: {
                                                    backgroundColor: colorOption.value,
                                                    ringOffsetColor: 'var(--bg-elevated)'
                                                }, title: colorOption.name }, colorOption.value))) })] })] }), _jsxs("div", { className: "flex gap-3 p-4 border-t", style: { borderColor: 'var(--border-color)' }, children: [_jsx("button", { onClick: closeQuickEditModal, className: "flex-1 px-4 py-2 rounded-lg font-medium transition-all", style: {
                                        backgroundColor: 'var(--bg-base)',
                                        color: 'var(--text-secondary)',
                                        border: '1px solid var(--border-color)'
                                    }, children: "Cancel" }), _jsx("button", { onClick: saveQuickEdit, disabled: loading || !quickEditStep.label.trim(), className: "flex-1 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: '#EC4899', color: 'white' }, children: loading ? 'Saving...' : 'Save' })] })] }) })), showEventModal && (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "w-full max-w-2xl rounded-lg max-h-[90vh] overflow-y-auto", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "sticky top-0 flex items-center justify-between p-6 border-b", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: [_jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--text-primary)' }, children: selectedEvent ? 'Edit Event' : 'Create Event' }), _jsx("button", { onClick: closeModal, className: "p-1 rounded hover:bg-white/10", children: _jsx(X, { className: "w-5 h-5", style: { color: 'var(--text-tertiary)' } }) })] }), _jsxs("div", { className: "p-6 space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Event Title *" }), _jsx("input", { type: "text", value: eventForm.label || '', onChange: (e) => setEventForm({ ...eventForm, label: e.target.value }), placeholder: "Enter event title", className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", id: "is-habit", checked: eventForm.isbuildhabit || false, onChange: (e) => setEventForm({ ...eventForm, isbuildhabit: e.target.checked }), className: "rounded" }), _jsxs("label", { htmlFor: "is-habit", className: "text-sm flex items-center gap-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(Repeat, { className: "w-4 h-4" }), "Mark as recurring habit"] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Description" }), _jsx("textarea", { value: eventForm.description || '', onChange: (e) => setEventForm({ ...eventForm, description: e.target.value }), placeholder: "Add event description", rows: 3, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Start Time *" }), _jsx("input", { type: "datetime-local", value: eventForm.scheduled_start_time ? utcToDatetimeLocal(eventForm.scheduled_start_time, userTimezone) : '', onChange: (e) => {
                                                        const value = e.target.value;
                                                        if (value) {
                                                            setEventForm({ ...eventForm, scheduled_start_time: datetimeLocalToUtc(value, userTimezone) });
                                                        }
                                                    }, className: "w-full px-4 py-2 rounded-lg mb-2", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    } }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { type: "button", onClick: () => {
                                                                if (eventForm.scheduled_start_time) {
                                                                    const zonedDate = utcToUserTimezone(eventForm.scheduled_start_time, userTimezone);
                                                                    zonedDate.setDate(zonedDate.getDate() - 1);
                                                                    setEventForm({ ...eventForm, scheduled_start_time: userTimezoneToUtc(zonedDate, userTimezone) });
                                                                }
                                                            }, className: "flex-1 px-3 py-1.5 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-secondary)'
                                                            }, children: "-1" }), _jsx("button", { type: "button", onClick: () => {
                                                                if (eventForm.scheduled_start_time) {
                                                                    const now = new Date();
                                                                    const zonedDate = utcToUserTimezone(eventForm.scheduled_start_time, userTimezone);
                                                                    zonedDate.setFullYear(now.getFullYear());
                                                                    zonedDate.setMonth(now.getMonth());
                                                                    zonedDate.setDate(now.getDate());
                                                                    setEventForm({ ...eventForm, scheduled_start_time: userTimezoneToUtc(zonedDate, userTimezone) });
                                                                }
                                                            }, className: "flex-1 px-3 py-1.5 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-secondary)'
                                                            }, children: "\u00B10" }), _jsx("button", { type: "button", onClick: () => {
                                                                if (eventForm.scheduled_start_time) {
                                                                    const zonedDate = utcToUserTimezone(eventForm.scheduled_start_time, userTimezone);
                                                                    zonedDate.setDate(zonedDate.getDate() + 1);
                                                                    setEventForm({ ...eventForm, scheduled_start_time: userTimezoneToUtc(zonedDate, userTimezone) });
                                                                }
                                                            }, className: "flex-1 px-3 py-1.5 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-secondary)'
                                                            }, children: "+1" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "End Time *" }), _jsx("input", { type: "datetime-local", value: eventForm.scheduled_end_time ? utcToDatetimeLocal(eventForm.scheduled_end_time, userTimezone) : '', onChange: (e) => {
                                                        const value = e.target.value;
                                                        if (value) {
                                                            setEventForm({ ...eventForm, scheduled_end_time: datetimeLocalToUtc(value, userTimezone) });
                                                        }
                                                    }, className: "w-full px-4 py-2 rounded-lg mb-2", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    } }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { type: "button", onClick: () => {
                                                                if (eventForm.scheduled_end_time) {
                                                                    const zonedDate = utcToUserTimezone(eventForm.scheduled_end_time, userTimezone);
                                                                    zonedDate.setDate(zonedDate.getDate() - 1);
                                                                    setEventForm({ ...eventForm, scheduled_end_time: userTimezoneToUtc(zonedDate, userTimezone) });
                                                                }
                                                            }, className: "flex-1 px-3 py-1.5 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-secondary)'
                                                            }, children: "-1" }), _jsx("button", { type: "button", onClick: () => {
                                                                if (eventForm.scheduled_end_time) {
                                                                    const now = new Date();
                                                                    const zonedDate = utcToUserTimezone(eventForm.scheduled_end_time, userTimezone);
                                                                    zonedDate.setFullYear(now.getFullYear());
                                                                    zonedDate.setMonth(now.getMonth());
                                                                    zonedDate.setDate(now.getDate());
                                                                    setEventForm({ ...eventForm, scheduled_end_time: userTimezoneToUtc(zonedDate, userTimezone) });
                                                                }
                                                            }, className: "flex-1 px-3 py-1.5 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-secondary)'
                                                            }, children: "\u00B10" }), _jsx("button", { type: "button", onClick: () => {
                                                                if (eventForm.scheduled_end_time) {
                                                                    const zonedDate = utcToUserTimezone(eventForm.scheduled_end_time, userTimezone);
                                                                    zonedDate.setDate(zonedDate.getDate() + 1);
                                                                    setEventForm({ ...eventForm, scheduled_end_time: userTimezoneToUtc(zonedDate, userTimezone) });
                                                                }
                                                            }, className: "flex-1 px-3 py-1.5 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid var(--border-color)',
                                                                color: 'var(--text-secondary)'
                                                            }, children: "+1" })] })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Duration (minutes)" }), _jsx("div", { className: "grid grid-cols-4 gap-2", children: [15, 30, 45, 60, 90, 120].map((duration) => (_jsxs("button", { onClick: () => {
                                                    if (eventForm.scheduled_start_time) {
                                                        const start = new Date(eventForm.scheduled_start_time);
                                                        const end = new Date(start.getTime() + duration * 60000);
                                                        setEventForm({ ...eventForm, scheduled_end_time: end.toISOString(), duration });
                                                    }
                                                }, className: "p-2 rounded-lg text-sm transition-all", style: {
                                                    backgroundColor: eventForm.duration === duration ? eventForm.color : 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)',
                                                    color: eventForm.duration === duration ? 'white' : 'var(--text-primary)'
                                                }, children: [duration, "m"] }, duration))) })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", id: "all-day", checked: eventForm.all_day || false, onChange: (e) => setEventForm({ ...eventForm, all_day: e.target.checked }), className: "rounded" }), _jsx("label", { htmlFor: "all-day", className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "All day event" })] }), eventForm.isbuildhabit && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Frequency" }), _jsx("div", { className: "grid grid-cols-2 gap-2", children: [
                                                { value: "Every day!", label: "Daily" },
                                                { value: "Monday, Tuesday, Wednesday, Thursday, Friday", label: "Weekdays" },
                                                { value: "Saturday, Sunday", label: "Weekends" },
                                                { value: "", label: "Custom" },
                                            ].map((freq) => (_jsx("button", { onClick: () => setEventForm({ ...eventForm, frequency: freq.value }), className: "p-2 rounded-lg text-sm transition-all", style: {
                                                    backgroundColor: eventForm.frequency === freq.value ? eventForm.color : 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)',
                                                    color: eventForm.frequency === freq.value ? 'white' : 'var(--text-primary)'
                                                }, children: freq.label }, freq.label))) })] })), selectedEvent && (_jsxs("div", { className: "p-4 rounded-lg", style: {
                                        backgroundColor: 'var(--bg-secondary)',
                                        border: '1px solid var(--border-color)'
                                    }, children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Convert Event Type" }), _jsxs("div", { className: "mb-3 p-2 rounded", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)'
                                            }, children: [_jsxs("div", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: ["Current type: ", _jsx("span", { style: { color: 'var(--text-primary)', fontWeight: 600 }, children: isTaskListMode ? 'Task List' : isLengthMode ? 'Length' : 'Step (Activity)' })] }), _jsx("div", { className: "text-xs mt-1", style: { color: 'var(--text-muted)' }, children: isTaskListMode || isLengthMode
                                                        ? `${linkedSteps.length} connected ${linkedSteps.length === 1 ? 'step' : 'steps'}`
                                                        : `${breaths.length} connected ${breaths.length === 1 ? 'breath' : 'breaths'}` })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("div", { className: "text-xs mb-2", style: { color: 'var(--text-muted)' }, children: "Convert to:" }), (isTaskListMode || isLengthMode) && (_jsxs("button", { type: "button", onClick: () => handleConvertEventType('step'), className: "w-full px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80", style: {
                                                        backgroundColor: '#3B82F6',
                                                        color: 'white',
                                                        border: 'none'
                                                    }, children: ["Convert to Step (Activity)", _jsxs("div", { className: "text-xs mt-1 opacity-80", children: [linkedSteps.length, " ", linkedSteps.length === 1 ? 'step' : 'steps', " will become ", linkedSteps.length === 1 ? 'breath' : 'breaths'] })] })), !isTaskListMode && (_jsxs("button", { type: "button", onClick: () => handleConvertEventType('task_list'), className: "w-full px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80", style: {
                                                        backgroundColor: '#8B5CF6',
                                                        color: 'white',
                                                        border: 'none'
                                                    }, children: ["Convert to Task List", _jsx("div", { className: "text-xs mt-1 opacity-80", children: isLengthMode
                                                                ? `${linkedSteps.length} ${linkedSteps.length === 1 ? 'step' : 'steps'} will be preserved`
                                                                : `${breaths.length} ${breaths.length === 1 ? 'breath' : 'breaths'} will become ${breaths.length === 1 ? 'step' : 'steps'}` })] })), !isLengthMode && (_jsxs("button", { type: "button", onClick: () => handleConvertEventType('length'), className: "w-full px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80", style: {
                                                        backgroundColor: '#10B981',
                                                        color: 'white',
                                                        border: 'none'
                                                    }, children: ["Convert to Length", _jsx("div", { className: "text-xs mt-1 opacity-80", children: isTaskListMode
                                                                ? `${linkedSteps.length} ${linkedSteps.length === 1 ? 'step' : 'steps'} will be preserved`
                                                                : `${breaths.length} ${breaths.length === 1 ? 'breath' : 'breaths'} will become ${breaths.length === 1 ? 'step' : 'steps'}` })] }))] }), _jsx("div", { className: "mt-3 p-2 rounded text-xs", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                color: 'var(--text-muted)',
                                                border: '1px solid var(--border-color)'
                                            }, children: "Converting will create a new event of the selected type and transfer all connected items appropriately." })] })), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(MapPin, { className: "w-4 h-4 inline mr-1" }), "Location"] }), _jsx("input", { type: "text", value: eventForm.location || '', onChange: (e) => setEventForm({ ...eventForm, location: e.target.value }), placeholder: "Add location", className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(Users, { className: "w-4 h-4 inline mr-1" }), "Attendees (comma-separated emails)"] }), _jsx("input", { type: "text", value: eventForm.attendees?.join(', ') || '', onChange: (e) => setEventForm({
                                                ...eventForm,
                                                attendees: e.target.value.split(',').map(email => email.trim()).filter(Boolean)
                                            }), placeholder: "email1@example.com, email2@example.com", className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Event Color" }), _jsx("div", { className: "flex gap-2", children: EVENT_COLORS.map((colorOption) => (_jsx("button", { onClick: () => setEventForm({ ...eventForm, color: colorOption.value }), className: "w-8 h-8 rounded-full transition-all", style: {
                                                    backgroundColor: colorOption.value,
                                                    border: eventForm.color === colorOption.value ? '3px solid white' : 'none',
                                                    outline: eventForm.color === colorOption.value ? '2px solid var(--text-primary)' : 'none'
                                                }, title: colorOption.name }, colorOption.value))) })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Notes" }), _jsx("textarea", { value: eventForm.habit_notes || '', onChange: (e) => setEventForm({ ...eventForm, habit_notes: e.target.value }), placeholder: "Add notes, meeting links, or details...", rows: 3, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(Sparkles, { className: "w-4 h-4 inline mr-1" }), "Mantra"] }), _jsx("textarea", { value: eventForm.mantra || '', onChange: (e) => setEventForm({ ...eventForm, mantra: e.target.value }), placeholder: "Your personal mantra or affirmation for this step...", rows: 2, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Starting Ritual" }), _jsx("textarea", { value: eventForm.starting_ritual || '', onChange: (e) => setEventForm({ ...eventForm, starting_ritual: e.target.value }), placeholder: "Ritual or preparation before starting this step...", rows: 2, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Ending Ritual" }), _jsx("textarea", { value: eventForm.ending_ritual || '', onChange: (e) => setEventForm({ ...eventForm, ending_ritual: e.target.value }), placeholder: "Ritual or reflection after completing this step...", rows: 2, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(Music, { className: "w-4 h-4 inline mr-1" }), "Background Sound"] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: "text", value: eventForm.audio_url || '', onChange: (e) => setEventForm({ ...eventForm, audio_url: e.target.value || null }), placeholder: "Sound URL (e.g., /sounds/rain-woodlands.m4a)", className: "flex-1 px-4 py-2 rounded-lg", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    } }), _jsxs("select", { value: eventForm.audio_type || 'ambient', onChange: (e) => setEventForm({ ...eventForm, audio_type: e.target.value || null }), className: "px-4 py-2 rounded-lg", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    }, children: [_jsx("option", { value: "ambient", children: "Ambient" }), _jsx("option", { value: "focus", children: "Focus" }), _jsx("option", { value: "meditation", children: "Meditation" }), _jsx("option", { value: "music", children: "Music" })] })] })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(Zap, { className: "w-4 h-4 inline mr-1" }), "Energy Level"] }), _jsx("div", { className: "grid grid-cols-5 gap-2", children: [
                                                { value: 1, icon: '😴', label: 'Very Low' },
                                                { value: 2, icon: '😐', label: 'Low' },
                                                { value: 3, icon: '🙂', label: 'Medium' },
                                                { value: 4, icon: '😊', label: 'High' },
                                                { value: 5, icon: '⚡', label: 'Very High' },
                                            ].map((level) => (_jsxs("button", { onClick: () => setEventForm({ ...eventForm, energy_level: level.value }), className: "p-3 rounded-lg text-center transition-all", style: {
                                                    backgroundColor: eventForm.energy_level === level.value ? eventForm.color : 'var(--bg-base)',
                                                    border: `2px solid ${eventForm.energy_level === level.value ? eventForm.color : 'var(--border-color)'}`,
                                                    color: eventForm.energy_level === level.value ? 'white' : 'var(--text-primary)'
                                                }, title: level.label, children: [_jsx("div", { className: "text-2xl", children: level.icon }), _jsx("div", { className: "text-xs mt-1", children: level.label })] }, level.value))) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Priority Letter" }), _jsxs("select", { value: eventForm.priority_letter || 'None', onChange: (e) => setEventForm({ ...eventForm, priority_letter: e.target.value }), className: "w-full px-4 py-2 rounded-lg", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    }, children: [_jsx("option", { value: "None", children: "None" }), _jsx("option", { value: "S", children: "S (Strategic)" }), _jsx("option", { value: "E", children: "E (Essential)" }), _jsx("option", { value: "R", children: "R (Routine)" }), _jsx("option", { value: "N", children: "N (Non-Urgent)" }), _jsx("option", { value: "T", children: "T (Trivial)" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Priority Rank" }), _jsx("input", { type: "number", value: eventForm.priority_rank || 0, onChange: (e) => setEventForm({ ...eventForm, priority_rank: parseInt(e.target.value) || 0 }), min: "0", className: "w-full px-4 py-2 rounded-lg", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    } })] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", id: "locked", checked: eventForm.locked || false, onChange: (e) => setEventForm({ ...eventForm, locked: e.target.checked }), className: "rounded" }), _jsxs("label", { htmlFor: "locked", className: "text-sm flex items-center gap-2", style: { color: 'var(--text-secondary)' }, children: [_jsx(Lock, { className: "w-4 h-4" }), "Locked (prevent accidental changes)"] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Journal Entry" }), _jsx("textarea", { value: eventForm.journal_content || '', onChange: (e) => setEventForm({ ...eventForm, journal_content: e.target.value }), placeholder: "Write your thoughts, reflections, or notes about this step...", rows: 3, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "CBT Notes" }), _jsx("p", { className: "text-xs mb-2", style: { color: 'var(--text-tertiary)' }, children: "Situation / Event \u2192 Automatic Thoughts or Emotions \u2192 Worldview / History \u2192 Constructive Response \u2192 Outcome" }), _jsx("textarea", { value: eventForm.cbt_notes || '', onChange: (e) => setEventForm({ ...eventForm, cbt_notes: e.target.value }), placeholder: "Enter your CBT notes here...", rows: 3, className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            } })] }), _jsxs("div", { className: "p-4 rounded-lg", style: { backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }, children: [_jsx("label", { className: "block text-sm font-semibold mb-3 uppercase tracking-wide", style: { color: 'var(--text-primary)' }, children: "DDDPT Framework" }), _jsxs("div", { className: "space-y-3", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-xs font-semibold mb-1 uppercase", style: { color: '#06B6D4' }, children: "Discover" }), _jsx("textarea", { value: eventForm.dddpt_discover || '', onChange: (e) => setEventForm({ ...eventForm, dddpt_discover: e.target.value }), placeholder: "What did you discover or learn?", rows: 2, className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid rgba(6, 182, 212, 0.3)',
                                                                color: 'var(--text-primary)'
                                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-semibold mb-1 uppercase", style: { color: '#3B82F6' }, children: "Define" }), _jsx("textarea", { value: eventForm.dddpt_define || '', onChange: (e) => setEventForm({ ...eventForm, dddpt_define: e.target.value }), placeholder: "What did you define or clarify?", rows: 2, className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                                                color: 'var(--text-primary)'
                                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-semibold mb-1 uppercase", style: { color: '#8B5CF6' }, children: "Ideate" }), _jsx("textarea", { value: eventForm.dddpt_ideate || '', onChange: (e) => setEventForm({ ...eventForm, dddpt_ideate: e.target.value }), placeholder: "What ideas or solutions emerged?", rows: 2, className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid rgba(139, 92, 246, 0.3)',
                                                                color: 'var(--text-primary)'
                                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-semibold mb-1 uppercase", style: { color: '#EC4899' }, children: "Prototype" }), _jsx("textarea", { value: eventForm.dddpt_prototype || '', onChange: (e) => setEventForm({ ...eventForm, dddpt_prototype: e.target.value }), placeholder: "What did you prototype or try?", rows: 2, className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid rgba(236, 72, 153, 0.3)',
                                                                color: 'var(--text-primary)'
                                                            } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs font-semibold mb-1 uppercase", style: { color: '#10B981' }, children: "Test" }), _jsx("textarea", { value: eventForm.dddpt_test || '', onChange: (e) => setEventForm({ ...eventForm, dddpt_test: e.target.value }), placeholder: "What did you test or validate?", rows: 2, className: "w-full px-3 py-2 rounded text-sm", style: {
                                                                backgroundColor: 'var(--bg-base)',
                                                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                                                color: 'var(--text-primary)'
                                                            } })] })] })] }), _jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between mb-3", children: [_jsxs("label", { className: "block text-sm font-medium", style: { color: 'var(--text-secondary)' }, children: [_jsx(Bell, { className: "w-4 h-4 inline mr-1" }), "Alerts & Reminders"] }), _jsx("span", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: "\uD83D\uDD14 Nudge" })] }), _jsx("div", { className: "space-y-2", children: [
                                                { id: 'start', label: 'At start of task' },
                                                { id: 'end', label: 'At end of task' },
                                                { id: '1min', label: '1m before start' },
                                                { id: '5min', label: '5m before start' },
                                                { id: '10min', label: '10m before start' },
                                            ].map((alert) => {
                                                const isSelected = eventForm.alerts?.includes(alert.id) || false;
                                                return (_jsxs("button", { type: "button", onClick: () => {
                                                        const currentAlerts = eventForm.alerts || [];
                                                        const newAlerts = isSelected
                                                            ? currentAlerts.filter(a => a !== alert.id)
                                                            : [...currentAlerts, alert.id];
                                                        setEventForm({ ...eventForm, alerts: newAlerts });
                                                    }, className: `w-full flex items-center justify-between p-3 rounded-lg transition-all ${isSelected
                                                        ? 'bg-blue-500/20 border-blue-500/50'
                                                        : 'bg-white/5 border-transparent hover:bg-white/10'} border`, style: {
                                                        backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.2)' : 'var(--bg-base)',
                                                        borderColor: isSelected ? 'rgba(59, 130, 246, 0.5)' : 'var(--border-color)'
                                                    }, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Bell, { className: "w-4 h-4", style: { color: isSelected ? '#3B82F6' : 'var(--text-tertiary)' } }), _jsx("span", { className: "text-sm", style: { color: 'var(--text-primary)' }, children: alert.label })] }), isSelected && (_jsx(X, { className: "w-4 h-4", style: { color: '#3B82F6' }, onClick: (e) => {
                                                                e.stopPropagation();
                                                                const currentAlerts = eventForm.alerts || [];
                                                                setEventForm({ ...eventForm, alerts: currentAlerts.filter(a => a !== alert.id) });
                                                            } }))] }, alert.id));
                                            }) })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: (isTaskListMode || isLengthMode) ? 'Sub-tasks (Steps)' : 'Sub-tasks (Breaths)' }), _jsxs("div", { className: "flex gap-2 mb-3", children: [_jsx("input", { type: "text", value: (isTaskListMode || isLengthMode) ? newStepName : newBreathName, onChange: (e) => (isTaskListMode || isLengthMode) ? setNewStepName(e.target.value) : setNewBreathName(e.target.value), onKeyDown: (e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            (isTaskListMode || isLengthMode) ? handleAddLinkedStep() : handleAddBreath();
                                                        }
                                                    }, placeholder: (isTaskListMode || isLengthMode) ? "Add a step..." : "Add a sub-task...", className: "flex-1 px-4 py-2 rounded-lg", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-primary)'
                                                    } }), _jsx(Button, { onClick: (isTaskListMode || isLengthMode) ? handleAddLinkedStep : handleAddBreath, disabled: (isTaskListMode || isLengthMode) ? !newStepName.trim() : !newBreathName.trim(), variant: "default", size: "sm", children: _jsx(Plus, { className: "w-4 h-4" }) })] }), (isTaskListMode || isLengthMode) ? (
                                        /* Linked Steps for Task Lists and Lengths */
                                        linkedSteps.length > 0 && (_jsx("div", { className: "space-y-2", children: linkedSteps.map((step, index) => (_jsxs("div", { className: "flex items-center gap-3 p-3 rounded-lg", style: {
                                                    backgroundColor: 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)'
                                                }, children: [_jsx(List, { className: "w-4 h-4", style: { color: 'var(--text-tertiary)' } }), _jsx("span", { className: "flex-1", style: { color: 'var(--text-primary)' }, children: step.label }), _jsxs("span", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: [step.duration || 60, " min"] }), _jsx(Button, { onClick: () => handleRemoveLinkedStep(index), variant: "ghost", size: "sm", className: "text-red-500 hover:text-red-600", children: _jsx(Trash2, { className: "w-4 h-4" }) })] }, index))) }))) : (
                                        /* Breaths for regular Steps */
                                        breaths.length > 0 && (_jsx("div", { className: "space-y-2", children: breaths.map((breath, index) => (_jsxs("div", { className: "flex items-center gap-3 p-3 rounded-lg", style: {
                                                    backgroundColor: 'var(--bg-base)',
                                                    border: '1px solid var(--border-color)'
                                                }, children: [_jsx("input", { type: "checkbox", checked: breath.completed || false, onChange: () => handleToggleBreathComplete(index), className: "w-4 h-4 rounded cursor-pointer", style: { accentColor: eventForm.color || '#3B82F6' } }), _jsx("span", { className: `flex-1 ${breath.completed ? 'line-through opacity-60' : ''}`, style: { color: 'var(--text-primary)' }, children: breath.name }), _jsxs("span", { className: "text-xs", style: { color: 'var(--text-tertiary)' }, children: ["~", Math.round((breath.time_estimation_seconds || 300) / 60), " min"] }), _jsx(Button, { onClick: () => handleRemoveBreath(index), variant: "ghost", size: "sm", className: "text-red-500 hover:text-red-600", children: _jsx(Trash2, { className: "w-4 h-4" }) })] }, index))) })))] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-secondary)' }, children: "Status" }), _jsxs("select", { value: eventForm.status || 'confirmed', onChange: (e) => setEventForm({ ...eventForm, status: e.target.value }), className: "w-full px-4 py-2 rounded-lg", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                border: '1px solid var(--border-color)',
                                                color: 'var(--text-primary)'
                                            }, children: [_jsx("option", { value: "confirmed", children: "Confirmed" }), _jsx("option", { value: "tentative", children: "Tentative" }), _jsx("option", { value: "cancelled", children: "Cancelled" })] })] })] }), _jsxs("div", { className: "sticky bottom-0 flex items-center justify-between p-6 border-t", style: { borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: [_jsx("div", { className: "flex items-center gap-3", children: selectedEvent && (_jsxs(_Fragment, { children: [_jsxs(Button, { onClick: unscheduleEvent, variant: "ghost", className: "text-orange-500 hover:text-orange-600", disabled: loading, children: [_jsx(CalendarClock, { className: "w-4 h-4 mr-2" }), "Unschedule"] }), _jsxs(Button, { onClick: () => initiateDelete(selectedEvent), variant: "ghost", className: "text-red-500 hover:text-red-600", disabled: loading, children: [_jsx(Trash2, { className: "w-4 h-4 mr-2" }), "Delete Event"] })] })) }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { onClick: closeModal, variant: "ghost", disabled: loading, children: "Cancel" }), _jsx(Button, { onClick: saveEvent, disabled: loading || !eventForm.label || !eventForm.scheduled_start_time || !eventForm.scheduled_end_time, style: { backgroundColor: '#EC4899', color: 'white' }, children: loading ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-4 h-4 mr-2 animate-spin" }), "Saving..."] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { className: "w-4 h-4 mr-2" }), selectedEvent ? 'Update Event' : 'Create Event'] })) })] })] })] }) })), showUpdateScopeModal && selectedEvent && (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "w-full max-w-md rounded-lg p-6", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "flex items-start gap-3 mb-4", children: [_jsx("div", { className: "p-2 rounded-lg", style: { backgroundColor: '#3B82F620' }, children: _jsx(Edit2, { className: "w-6 h-6", style: { color: '#3B82F6' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h2", { className: "text-xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Update Recurring Event" }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Which instances would you like to update?" })] })] }), _jsxs("div", { className: "space-y-3 mb-6", children: [_jsx("button", { onClick: async () => {
                                        await performSaveEventWithScope('this');
                                        setShowUpdateScopeModal(false);
                                    }, disabled: loading, className: "w-full p-4 rounded-lg border text-left hover:bg-opacity-80 transition-all", style: {
                                        backgroundColor: updateScope === 'this' ? '#3B82F620' : 'var(--bg-base)',
                                        borderColor: updateScope === 'this' ? '#3B82F6' : 'var(--border-color)',
                                        borderWidth: '2px'
                                    }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "w-5 h-5 rounded-full border-2 mt-0.5", style: { borderColor: updateScope === 'this' ? '#3B82F6' : 'var(--text-secondary)' } }), _jsxs("div", { children: [_jsx("div", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Update this event only" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "Changes will apply only to this instance" })] })] }) }), _jsx("button", { onClick: async () => {
                                        await performSaveEventWithScope('future');
                                        setShowUpdateScopeModal(false);
                                    }, disabled: loading, className: "w-full p-4 rounded-lg border text-left hover:bg-opacity-80 transition-all", style: {
                                        backgroundColor: updateScope === 'future' ? '#3B82F620' : 'var(--bg-base)',
                                        borderColor: updateScope === 'future' ? '#3B82F6' : 'var(--border-color)',
                                        borderWidth: '2px'
                                    }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "w-5 h-5 rounded-full border-2 mt-0.5", style: { borderColor: updateScope === 'future' ? '#3B82F6' : 'var(--text-secondary)' } }), _jsxs("div", { children: [_jsx("div", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Update this and future events" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "Changes will apply to this instance and all future occurrences" })] })] }) }), _jsx("button", { onClick: async () => {
                                        await performSaveEventWithScope('all');
                                        setShowUpdateScopeModal(false);
                                    }, disabled: loading, className: "w-full p-4 rounded-lg border text-left hover:bg-opacity-80 transition-all", style: {
                                        backgroundColor: updateScope === 'all' ? '#3B82F620' : 'var(--bg-base)',
                                        borderColor: updateScope === 'all' ? '#3B82F6' : 'var(--border-color)',
                                        borderWidth: '2px'
                                    }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "w-5 h-5 rounded-full border-2 mt-0.5", style: { borderColor: updateScope === 'all' ? '#3B82F6' : 'var(--text-secondary)' } }), _jsxs("div", { children: [_jsx("div", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Update all events" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "Changes will apply to all instances of this recurring event" })] })] }) })] }), _jsx("div", { className: "flex gap-3", children: _jsx(Button, { onClick: () => setShowUpdateScopeModal(false), variant: "outline", disabled: loading, className: "flex-1", children: "Cancel" }) })] }) })), showDeleteModal && eventToDelete && (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "w-full max-w-md rounded-lg p-6", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "flex items-start gap-3 mb-4", children: [_jsx("div", { className: "p-2 rounded-lg", style: { backgroundColor: '#EF444420' }, children: _jsx(Trash2, { className: "w-6 h-6", style: { color: '#EF4444' } }) }), _jsxs("div", { className: "flex-1", children: [_jsx("h2", { className: "text-xl font-semibold mb-2", style: { color: 'var(--text-primary)' }, children: "Delete Recurring Event" }), _jsxs("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: ["\"", eventToDelete.label, "\" is a recurring habit. How would you like to delete it?"] })] })] }), _jsxs("div", { className: "space-y-3 mb-6", children: [_jsx("button", { onClick: () => deleteEventConfirmed(eventToDelete.id, 'this-instance'), disabled: loading, className: "w-full p-4 rounded-lg border text-left hover:bg-opacity-80 transition-all", style: {
                                        backgroundColor: 'var(--bg-base)',
                                        borderColor: 'var(--border-color)'
                                    }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "w-5 h-5 rounded-full border-2 mt-0.5", style: { borderColor: 'var(--text-secondary)' } }), _jsxs("div", { children: [_jsx("div", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Delete this instance only" }), _jsxs("div", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: ["Remove only this occurrence on ", eventToDelete.scheduled_start_time ? new Date(eventToDelete._instanceDate || eventToDelete.scheduled_start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'this date'] })] })] }) }), _jsx("button", { onClick: () => deleteEventConfirmed(eventToDelete.id, 'all-instances'), disabled: loading, className: "w-full p-4 rounded-lg border text-left hover:bg-opacity-80 transition-all", style: {
                                        backgroundColor: 'var(--bg-base)',
                                        borderColor: 'var(--border-color)'
                                    }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "w-5 h-5 rounded-full border-2 mt-0.5", style: { borderColor: 'var(--text-secondary)' } }), _jsxs("div", { children: [_jsx("div", { className: "font-medium mb-1", style: { color: 'var(--text-primary)' }, children: "Delete all instances" }), _jsx("div", { className: "text-xs", style: { color: 'var(--text-secondary)' }, children: "Remove this recurring habit completely" })] })] }) })] }), _jsx("div", { className: "flex items-center justify-end gap-3", children: _jsx(Button, { onClick: () => {
                                    setShowDeleteModal(false);
                                    setEventToDelete(null);
                                }, variant: "ghost", disabled: loading, children: "Cancel" }) })] }) }))] }));
}
